import { useState, useEffect, useRef, Fragment } from "react";

// Anchos FIJOS de los ejes izquierdo/derecho de la gráfica (en px). Se usan
// también para armar la tabla de abajo con rejilla CSS (grid), reservando
// exactamente el mismo espacio a los costados — así cada columna de mes de
// la tabla queda pixel a pixel debajo de su barra correspondiente en la
// gráfica, sin importar el ancho de pantalla.
const GRAFICA_GUTTER_IZQ = 90;
const GRAFICA_GUTTER_DER = 50;

// Ancho fijo de la columna "Total" en la tabla de abajo. Esta columna NO
// existe en la gráfica (no es un mes), así que si la tabla ocupa el mismo
// ancho total que la gráfica pero le agrega esta columna extra al final,
// la zona de meses de la tabla queda GRAFICA_TOTAL_COL px más angosta que
// la zona de dibujo de la gráfica, y las columnas de mes se van corriendo
// (desalineación creciente de izquierda a derecha). Por eso la gráfica se
// envuelve en un contenedor que le resta este mismo ancho (ver
// GraficaChart más abajo), para que lo que SÍ se compara — ejes + meses —
// mida exactamente lo mismo en los dos.
const GRAFICA_TOTAL_COL = 82;

// ── Carga Chart.js desde CDN una sola vez ──────────────────────
let chartJsLoaded = false;
let chartJsLoading = false;
let chartJsCallbacks = [];

function loadChartJs(callback) {
  if (chartJsLoaded) { callback(); return; }
  chartJsCallbacks.push(callback);
  if (chartJsLoading) return;
  chartJsLoading = true;
  const script = document.createElement("script");
  script.src = "https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js";
  script.onload = () => {
    chartJsLoaded = true;
    chartJsCallbacks.forEach(cb => cb());
    chartJsCallbacks = [];
  };
  document.head.appendChild(script);
}

// Dibuja un rectángulo redondeado (con fallback si el navegador no soporta
// ctx.roundRect todavía) — usado para la "placa" oscura de los % sobre la línea.
function trazarRectRedondeado(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
}

// Placa oscura redondeada con texto blanco en negrilla — estilo "TRF FIJO R3"
// (el % flotando en una cajita de color sobre cada punto de la línea).
function dibujarPlaca(ctx, texto, cx, cyBase, bg) {
  ctx.save();
  ctx.font = "bold 11px 'Segoe UI', sans-serif";
  const padX = 7, altoPlaca = 20;
  const anchoTexto = ctx.measureText(texto).width;
  const w = anchoTexto + padX * 2;
  const x = cx - w / 2;
  const y = cyBase - altoPlaca;
  ctx.fillStyle = bg;
  trazarRectRedondeado(ctx, x, y, w, altoPlaca, 5);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(texto, cx, y + altoPlaca / 2);
  ctx.restore();
}

// ── Plugin etiquetas sobre barras y puntos ──────────────────────
// Estilo "TRF FIJO R3": el valor de cada barra va CENTRADO dentro de la
// barra, y el % de cada línea va en una placa oscura redondeada flotando
// sobre el punto, para que se lea de un vistazo sin tapar las barras.
function makePlugin(coloresCump, colorProy) {
  return {
    id: "etq_" + Math.random(),
    afterDatasetsDraw(chart) {
      const ctx = chart.ctx;
      chart.data.datasets.forEach((ds, di) => {
        if (ds.label === "Meta" || ds.label === "Meta ref.") return;
        const meta = chart.getDatasetMeta(di);
        if (meta.hidden) return;
        const isPct  = ["% Cumplimiento","% Total ejecutado","% Cert. mismo mes"].includes(ds.label);
        const isProy = ds.label === "% Proyección" || ds.label === "% Proyección G1";
        const isCert = ["Cert. meta fecha","Cert. meta","Cert. mismo mes","Cert."].includes(ds.label);
        const isBack = ds.label === "Backlog";

        meta.data.forEach((el, i) => {
          const val = ds.data[i];
          if (val == null || val === 0) return;

          // ── Líneas de % → placa oscura flotando sobre el punto ──
          if (isPct || isProy) {
            const bg = isPct ? (coloresCump[i] || "#1f2937") : (colorProy || "#1f2937");
            dibujarPlaca(ctx, val + "%", el.x, el.y - 10, bg);
            return;
          }

          // ── Barras → valor centrado dentro de la barra ──
          const barTop  = el.y;
          const barBase = el.base ?? (el.y + (el.height || 0));
          const altoBarra = Math.abs(barBase - barTop);
          const color = isCert ? "#fff" : isBack ? "#fff" : "#1f2937";
          ctx.save();
          ctx.font = "bold 11px 'Segoe UI', sans-serif";
          ctx.fillStyle = color;
          ctx.textAlign = "center";
          if (altoBarra > 16) {
            // cabe el número dentro de la barra: lo centramos verticalmente
            ctx.textBaseline = "middle";
            ctx.fillText(val, el.x, (barTop + barBase) / 2);
          } else {
            // barra muy baja: lo ponemos justo encima para que no quede apretado
            ctx.textBaseline = "bottom";
            ctx.fillStyle = "#1f2937";
            ctx.fillText(val, el.x, barTop - 3);
          }
          ctx.restore();
        });
      });
    },
  };
}

// ── Componente gráfica con Chart.js CDN ─────────────────────────
function GraficaChart({ datos, colCert, colProy, showBacklog, metaArr, prioArr, hidePrio, showMeta, onArea }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);
  const [ready, setReady] = useState(chartJsLoaded);

  // onArea puede ser una función nueva en cada render del padre; la
  // guardamos en un ref para que el plugin de Chart.js (creado una sola
  // vez por efecto) siempre llame a la versión más reciente sin que haga
  // falta recrear la gráfica cada vez.
  const onAreaRef = useRef(onArea);
  onAreaRef.current = onArea;
  const lastAreaRef = useRef(null);

  useEffect(() => {
    if (!ready) { loadChartJs(() => setReady(true)); }
  }, [ready]);

  useEffect(() => {
    if (!ready || !canvasRef.current) return;
    const ChartJS = window.Chart;
    if (!ChartJS) return;

    if (chartRef.current) { chartRef.current.destroy(); }

    const labels  = datos.map(d => d.mes);
    const cert    = datos.map(d => d.cert  || 0);
    const back    = datos.map(d => d.back  || 0);
    const prio    = datos.map(d => d.prio  || 0);
    const meta    = datos.map(d => d.meta  || 19);
    const pct     = datos.map(d => d.pct   || 0);
    const proy    = datos.map(d => d.proy  ?? null);

    const colores = Array.isArray(colCert) ? colCert : cert.map(() => colCert || "#E30613");

    const metaLine = datos.map(d => d.metaLine || 0);

    // Eje derecho (%) dinámico: por defecto llega hasta 260%, igual que
    // antes, pero si al filtrar (p. ej. por un aliado con meta más chica)
    // el % de cumplimiento o la proyección superan ese techo, el eje crece
    // automáticamente para que la línea/los puntos nunca queden cortados
    // fuera del área visible de la gráfica.
    const Y2_MAX_DEFECTO = 260;
    const valoresPct = [...pct, ...proy].filter(v => v !== null && v !== undefined && isFinite(v));
    const maxPctDato = valoresPct.length ? Math.max(...valoresPct) : 0;
    const y2Max = maxPctDato > Y2_MAX_DEFECTO
      ? Math.ceil((maxPctDato * 1.15) / 50) * 50
      : Y2_MAX_DEFECTO;
    const datasets = [
      ...(!hidePrio ? [{ type:"bar", label:"Priorizados", data:prio, backgroundColor:"#cbd5e1", borderRadius:4, yAxisID:"y", order:6 }] : []),
      ...(showMeta ? [{ type:"bar", label:"Meta ref.", data:metaLine, backgroundColor:"#e2e8f0", borderRadius:4, yAxisID:"y", order:5 }] : []),
      { type:"bar",  label:"Cert.",            data:cert,  backgroundColor:"#E30613", borderRadius:4, yAxisID:"y",  order:3, ...(showBacklog ? {stack:"cert"} : {}) },
      ...(showBacklog ? [
        { type:"bar", label:"Backlog", data:back, backgroundColor:"#f97316", borderRadius:4, yAxisID:"y", order:2, stack:"cert" }
      ] : []),
      { type:"line", label:"Meta",             data:meta,  borderColor:"#9ca3af", borderWidth:1.5, borderDash:[5,4], pointRadius:0, fill:false, yAxisID:"y",  order:4 },
      { type:"line", label:"% Cumplimiento",   data:pct,   borderColor:"#1f2937", borderWidth:2.5, pointStyle:"circle", pointRadius:6, pointBackgroundColor:colores, pointBorderColor:"#fff", pointBorderWidth:2, fill:false, tension:0.3, yAxisID:"y2", order:1 },
      { type:"line", label:"% Proyección",     data:proy,  borderColor:colProy,   borderWidth:2,   borderDash:[6,3],   pointStyle:"rectRot", pointRadius:7, pointBackgroundColor:colProy, pointBorderColor:"#fff", pointBorderWidth:2, fill:false, tension:0, yAxisID:"y2", order:0, spanGaps:false },
    ];

    chartRef.current = new ChartJS(canvasRef.current, {
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode:"index", intersect:false },
        plugins: {
          legend: { display:false },
          tooltip: {
            backgroundColor:"#1f2937", titleColor:"#f9fafb", bodyColor:"#e5e7eb", padding:10, cornerRadius:8,
            position: "nearest",
            xAlign: "center",
            yAlign: "bottom",
            callbacks: {
              label: (ctx) => {
                const i = ctx.dataIndex;
                const lbl = ctx.dataset.label;
                if (lbl === "% Cumplimiento") {
                  const certSolo = cert[i] || 0;
                  const pctMeta  = Math.round(certSolo / (meta[i] || 1) * 100);
                  if (showBacklog) {
                    const totalEjec = certSolo + (back[i] || 0);
                    const pctTotal  = Math.round(totalEjec / (meta[i] || 1) * 100);
                    return [
                      ` Cumpl. vs Meta: ${pctMeta}%  (${certSolo}/${meta[i]})`,
                      ` Cumpl. c/Backlog: ${pctTotal}%  (${totalEjec}/${meta[i]})`,
                    ];
                  }
                  return ` Cumplimiento: ${pctMeta}%  (${certSolo}/${meta[i]})`;
                }
                if (lbl === "% Proyección")    return ` Proyección: ${ctx.parsed.y}%`;
                if (lbl === "Meta")            return ` Meta: ${meta[i]} nodos`;
                if (lbl === "Meta ref.")        return ` Meta: ${metaLine[i]} nodos`;
                if (lbl === "Priorizados")      return ` Priorizados: ${ctx.parsed.y}`;
                if (lbl === "Backlog")         return ` Backlog: +${ctx.parsed.y}`;
                if (lbl === "Cert.")           return ` Certificados: ${ctx.parsed.y}`;
                return ` ${lbl}: ${ctx.parsed.y}`;
              },
            },
          },
        },
        scales: {
          x:  { grid:{display:false}, ticks:{font:{size:11},color:"#6b7280",autoSkip:false} },
          y:  { position:"left",  beginAtZero:true, max:60,  grid:{color:"rgba(0,0,0,0.04)"}, ticks:{stepSize:10,font:{size:11},color:"#6b7280"}, afterFit:(axis)=>{ axis.width = GRAFICA_GUTTER_IZQ; } },
          y2: { position:"right", beginAtZero:true, max:y2Max, grid:{drawOnChartArea:false}, ticks:{font:{size:11},color:"#1f2937",callback:v=>v+"%",stepSize:50}, afterFit:(axis)=>{ axis.width = GRAFICA_GUTTER_DER; } },
        },
      },
      plugins: [makePlugin(colCert, colProy), {
        // Plugin "invisible": no dibuja nada, solo mide en cada layout/resize
        // el área real de dibujo de la gráfica (en px) y se la pasa al padre
        // vía onArea, para que la tabla de abajo use EXACTAMENTE esos mismos
        // px y sus columnas queden pixel a pixel debajo de cada barra/mes —
        // en vez de adivinar un ancho fijo que no siempre coincide.
        id: "reportarArea",
        afterLayout(chart) {
          // En el hook "resize" de Chart.js, chart.chartArea todavía no está
          // recalculado (eso pasa en el layout que viene justo después), así
          // que solo medimos aquí, en afterLayout, que se dispara también
          // tras cada resize (Chart.js hace resize -> update -> layout).
          if (!chart || !chart.chartArea) return;
          const left  = Math.round(chart.chartArea.left);
          const right = Math.round(chart.width - chart.chartArea.right);
          const prev  = lastAreaRef.current;
          if (!prev || prev.left !== left || prev.right !== right) {
            lastAreaRef.current = { left, right };
            if (onAreaRef.current) onAreaRef.current({ left, right });
          }
        },
      }],
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [ready, datos, colCert, colProy, showBacklog]);

  if (!ready) return (
    <div style={{height:"260px",display:"flex",alignItems:"center",justifyContent:"center",color:"#9ca3af",fontSize:"13px"}}>
      Cargando gráfica...
    </div>
  );
  return <div style={{position:"relative", height:"260px"}}><canvas ref={canvasRef} /></div>;
}

const CLARO_RED  = "#E30613";
const CLARO_DARK = "#0f172a";
const CLARO_MUTED= "#64748b";
const CLARO_LIGHT= "#94a3b8";
const CLARO_BG   = "#f1f5f9";
const VERDE      = "#16a34a";
const ROJO       = "#E30613";
const NARANJ     = "#f97316";
const META       = 19;

// ── Nombres de mes — dinámicos para cualquier año/mes ──────────
const _NOMBRES_MES = ["","Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

// Genera "Ene 2026", "Jun 2026", etc. para cualquier clave "YYYY-MM"
const getMesLabel = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return `${_NOMBRES_MES[parseInt(m)] || m} ${y}`;
};
// Genera "Ene", "Jun", etc.
const getMesCorto = (ym) => {
  if (!ym) return ym;
  const [, m] = ym.split("-");
  return _NOMBRES_MES[parseInt(m)] || m;
};

// Proxy: MESES_LABEL["2026-06"] → "Jun 2026" sin necesidad de actualizar manualmente
const MESES_LABEL = new Proxy({}, { get: (_, k) => getMesLabel(k) });
const MESES_CORTO = new Proxy({}, { get: (_, k) => getMesCorto(k) });

const mesMostrar = (ym) => {
  if (!ym) return "—";
  // Nuevo formato: DD/MM/YYYY → mostrar directo
  if (ym.includes("/")) return ym;
  // Formato YYYY-MM → "Mes YYYY"
  return getMesLabel(ym);
};

// Devuelve los días reales del mes (soporta años bisiestos automáticamente)
const getDiasMes = (ym) => {
  if (!ym) return 30;
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};
// Proxy: DIAS_MES["2026-06"] → 30, DIAS_MES["2028-02"] → 29 (bisiesto)
const DIAS_MES = new Proxy({}, { get: (_, k) => getDiasMes(k) });

const fmt = (n) => (n ?? 0).toLocaleString("es-CO");
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) : "0.0");

// ── KPI Card ───────────────────────────────────────────────────
function KpiCard({ label, valor, sub, color }) {
  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"18px 20px", flex:1, minWidth:"140px" }}>
      <div style={{ fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:"8px" }}>{label}</div>
      <div style={{ fontSize:"28px", fontWeight:"700", color:color||CLARO_DARK }}>{valor}</div>
      {sub && <div style={{ fontSize:"12px", color:CLARO_LIGHT, marginTop:"4px" }}>{sub}</div>}
    </div>
  );
}

// ── Badge tabla ────────────────────────────────────────────────
function Badge({ estado }) {
  const esCert  = estado === "Certificado";
  const esOtro  = estado === "Cert. otro mes";
  const esSE    = estado === "Sin entrega";
  const esRepr  = estado === "Reprogramado";
  const bg   = esCert ? "#f0fdf4" : esOtro ? "#eff6ff" : esSE ? "#fff7ed" : esRepr ? "#faf5ff" : "#f8fafc";
  const col  = esCert ? "#16a34a" : esOtro ? "#2563eb" : esSE ? "#f97316" : esRepr ? "#7c3aed" : "#E30613";
  const bord = esCert ? "#16a34a" : esOtro ? "white" : esSE ? "#ea580c" : esRepr ? "#a78bfa" : "#fca5a5";
  const txt  = esCert ? "✓ Certificado" : esOtro ? "↗ Cert. otro mes" : esSE ? "⚠ Sin entrega" : esRepr ? "🔁 Reprogramado" : "⏳ Pendiente";
  return (
    <span style={{ display:"inline-block", fontSize:"11px", fontWeight:"700", padding:"3px 10px", borderRadius:"20px", background:bg, color:col, border:`1px solid ${bord}` }}>
      {txt}
    </span>
  );
}

// ── Leyenda ────────────────────────────────────────────────────
function Leyenda({ items }) {
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:"10px", marginBottom:"12px" }}>
      {items.map(({ color, label, line, dash }) => (
        <span key={label} style={{ display:"flex", alignItems:"center", gap:"4px", fontSize:"11px", color:CLARO_MUTED }}>
          {line
            ? <span style={{ width:"18px", height:"2px", borderTop:`2px ${dash?"dashed":"solid"} ${color}`, display:"inline-block" }} />
            : <span style={{ width:"10px", height:"10px", borderRadius:"2px", background:color, display:"inline-block" }} />
          }
          {label}
        </span>
      ))}
    </div>
  );
}

// ── Tarjetas mensuales ─────────────────────────────────────────
function TablaResumenHorizontal({ mesesKeys, certArr, backArr, pctArr, colArr, pctMetaArr, showProy, proyPct, proyN, colorProy, proyPctMeta, showTotal, metaPorMes, reprArr, prioArr, metaArr, gutterIzq, gutterDer }) {
  const mesActIdx  = mesesKeys.length - 1;
  const totCert    = certArr.reduce((s,v) => s+(v||0), 0);
  const totBack    = backArr ? backArr.reduce((s,v) => s+(v||0), 0) : 0;
  const totRepr    = reprArr ? reprArr.reduce((s,v) => s+(v||0), 0) : 0;
  const totPrio    = prioArr ? prioArr.reduce((s,v) => s+(v||0), 0) : 0;
  const sumMeta    = metaPorMes ? metaPorMes.reduce((s,v) => s+(v||META), 0) : META * mesesKeys.length;
  const totPct     = totCert > 0 ? Math.round(totCert / sumMeta * 100) : 0;
  const totCol     = totPct >= 100 ? VERDE : ROJO;
  const totPctMeta = pctMetaArr && totCert > 0 ? Math.round(totCert / sumMeta * 100) : 0;
  const totColMeta = totPctMeta >= 100 ? VERDE : ROJO;

  // Filas (métricas), cada una se dibuja como una fila horizontal con un
  // valor por mes (columnas) — mismo dato que antes, solo que ahora la
  // tabla queda DEBAJO de la gráfica en vez de al lado, como en el ejemplo.
  const filas = [];
  if (metaArr) {
    filas.push({ label:"Meta", color:CLARO_RED, valor:(i)=>metaArr[i]||"—", total:metaArr.reduce((s,v)=>s+(v||0),0), totalColor:"#fca5a5" });
  } else if (prioArr) {
    filas.push({ label:"Priorizados", color:CLARO_DARK, valor:(i)=>prioArr[i]||"—", total:totPrio, totalColor:"#e2e8f0" });
  }
  if (!metaArr && reprArr) {
    filas.push({ label:"🔁 Reprogramados", color:"#7c3aed", valor:(i)=>(reprArr[i]>0 ? reprArr[i] : "—"), total: totRepr>0?totRepr:"—", totalColor:"#c4b5fd" });
  }
  filas.push({ label:"Certificados", color:VERDE, bold:true, valor:(i)=>certArr[i], total:totCert, totalColor:"#86efac" });
  if (pctMetaArr) {
    filas.push({
      label:"% Meta", esPct:true,
      valor:(i)=> pctMetaArr[i] != null ? `${pctMetaArr[i]}%${pctMetaArr[i]>=100?" ✓":" ✗"}` : "—",
      valorColor:(i)=> pctMetaArr[i]>=100 ? VERDE : ROJO,
      total:`${totPctMeta}%`, totalColor:totColMeta,
      proy: proyPctMeta != null ? `${proyPctMeta}%` : "—",
    });
  }
  if (backArr) {
    filas.push({ label:"Backlog", color:NARANJ, valor:(i)=> backArr[i]>0?`+${backArr[i]}`:"—", total: totBack>0?`+${totBack}`:"—", totalColor:"#fdba74" });
  }
  if (showTotal && backArr) {
    filas.push({ label:"Total", color:"#2563eb", valor:(i)=> certArr[i]+(backArr[i]||0), total: totCert+totBack, totalColor:"#93c5fd" });
  }
  filas.push({
    label: pctMetaArr ? "% Total" : "%", esPct:true,
    valor:(i)=> `${pctArr[i]}%${pctArr[i]>=100?" ✓":" ✗"}`,
    valorColor:(i)=> colArr[i],
    total:`${totPct}%`, totalColor:totCol,
    proy: `${proyPct}%`,
  });

  // Rejilla CSS con el ancho REAL medido del área de dibujo de la gráfica
  // (gutterIzq / gutterDer, que llegan desde GraficaChart vía onArea,
  // midiendo chart.chartArea en cada layout/resize) para que cada columna
  // de mes caiga exactamente debajo de su barra — la columna de la
  // izquierda ocupa el mismo espacio que el eje izquierdo de la gráfica, el
  // "colchón" de la derecha el mismo espacio que el eje derecho, y entre
  // esos dos puntos los meses se reparten en el mismo ancho que el área de
  // dibujo. Si todavía no ha llegado una medición real (primer render),
  // usamos los anchos por defecto como aproximación mientras tanto.
  // "Total" va al final de cada fila (igual que antes), pero por FUERA de
  // esa zona alineada con los meses — igual que la columna de etiquetas
  // (Priorizados/Certificados/%) va por fuera a la izquierda. La proyección
  // no es un mes real, así que se muestra aparte, en su propia tarjeta.
  const gIzq = gutterIzq != null ? gutterIzq : GRAFICA_GUTTER_IZQ;
  const gDer = gutterDer != null ? gutterDer : GRAFICA_GUTTER_DER;
  const gridTemplateColumns = `${gIzq}px repeat(${mesesKeys.length}, 1fr) ${gDer}px ${GRAFICA_TOTAL_COL}px`;

  const celdaBase = { padding:"7px 6px", fontSize:"12px", fontWeight:"600", borderBottom:"1px solid #f1f5f9", textAlign:"center", overflow:"hidden", textOverflow:"ellipsis" };
  const cabBase   = { padding:"7px 6px", fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, borderBottom:"2px solid #e2e8f0", textAlign:"center" };

  // Filas de la tarjeta de proyección aparte (solo las que tengan un valor
  // proyectado real, para no mostrar renglones vacíos)
  const filasProy = filas.filter(f => f.proy != null);

  return (
    <div style={{ marginTop:"14px" }}>
      <div style={{ fontSize:"10px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:".06em", marginBottom:"8px" }}>
        Resumen por mes — alineado con los meses de la gráfica
      </div>
      <div style={{ display:"grid", gridTemplateColumns, fontSize:"12px" }}>
        {/* ── Encabezado ── */}
        <div style={{ ...cabBase, background:CLARO_BG }} />
        {mesesKeys.map((key, i) => (
          <div key={key} style={{ ...cabBase, background: i===mesActIdx ? "#fff0f0" : CLARO_BG, color: i===mesActIdx ? CLARO_RED : CLARO_MUTED }}>
            {MESES_CORTO[key]||key}{i===mesActIdx ? " ●" : ""}
          </div>
        ))}
        <div style={{ ...cabBase, background:CLARO_BG }} />
        <div style={{ ...cabBase, background:"#1f2937", color:"white" }}>Total</div>

        {/* ── Filas de datos ── */}
        {filas.map((fila, fi) => {
          const bgFila = fi%2===0 ? "white" : "#f9fafb";
          return (
            <Fragment key={fila.label}>
              <div style={{ ...celdaBase, textAlign:"left", fontWeight:"700", color:CLARO_DARK, background:CLARO_BG, whiteSpace:"nowrap" }}>
                {fila.label}
              </div>
              {mesesKeys.map((key, i) => (
                <div key={key} style={{ ...celdaBase, fontWeight: fila.bold?"700":"600",
                  color: fila.valorColor ? fila.valorColor(i) : (fila.color || CLARO_DARK),
                  background: i===mesActIdx ? "#fcfcfd" : bgFila }}>
                  {fila.valor(i)}
                </div>
              ))}
              <div style={{ ...celdaBase, background:bgFila }} />
              <div style={{ padding:"7px 6px", textAlign:"center", fontWeight:"700", fontSize:"12px", color: fila.totalColor || "white", background:"#1f2937" }}>
                {fila.total}
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* ── Proyección — aparte, no es un mes real ── */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:"10px", marginTop:"10px" }}>
        {showProy && filasProy.length > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:"16px", flexWrap:"wrap", padding:"10px 14px", background:"#fff7f7", border:`1px dashed ${colorProy}`, borderRadius:"8px", flex:1, minWidth:"260px" }}>
            <span style={{ fontSize:"11px", fontWeight:"700", color:CLARO_RED, textTransform:"uppercase", letterSpacing:".04em" }}>
              📈 Proy. {MESES_CORTO[mesesKeys[mesActIdx]]||""}
            </span>
            <span style={{ fontSize:"12px", color:CLARO_MUTED }}>Certificados: <strong style={{ color:colorProy }}>{proyN}</strong></span>
            {filasProy.map(f => (
              <span key={f.label} style={{ fontSize:"12px", color:CLARO_MUTED }}>
                {f.label}: <strong style={{ color:colorProy }}>{f.proy}</strong>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────
// ── Barra de progreso ─────────────────────────────────────────
function BarraAvance({ pct, pctMeta, metaLabel, etiqueta, sinEntrega, esMesActual, bolsaCerrada, soloPendientes, pctConBacklog, backlogLabel }) {
  const getColor = (v) => v >= 100 ? "#16a34a" : v >= 75 ? "#f59e0b" : "#E30613";
  const getGrad  = (v) => v >= 100
    ? "linear-gradient(90deg, #16a34a, #22c55e)"
    : v >= 75
    ? "linear-gradient(90deg, #d97706, #f59e0b)"
    : "linear-gradient(90deg, #dc2626, #E30613)";

  const color  = getColor(pct);
  const label1 = etiqueta || "% Cumpl.";
  const isNodo = pctMeta == null && !esMesActual && !bolsaCerrada;

  // ── Bloque badge reutilizable ──
  const Badge = ({ texto, bg, color: c, icon }) => (
    <div style={{
      display:"inline-flex", alignItems:"center", gap:"5px",
      background: bg, border:`1px solid ${c}40`,
      borderRadius:"8px", padding:"4px 10px",
      fontSize:"11px", fontWeight:"700", color: c,
      marginTop:"2px"
    }}>
      <span>{icon}</span> {texto}
    </div>
  );

  // ── Barra estándar reutilizable ──
  const Barra = ({ valor, label, altura }) => {
    const col = getColor(valor);
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:"2px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1px" }}>
          <span style={{ fontSize:"9px", fontWeight:"700", color:"#475569", textTransform:"uppercase", letterSpacing:"0.04em" }}>
            {label}
          </span>
          <span style={{ fontSize:"11px", fontWeight:"800", color:col, background:col+"15", borderRadius:"4px", padding:"0px 5px", lineHeight:"16px" }}>
            {valor}%
          </span>
        </div>
        <div style={{ height: altura || "8px", background:"#e2e8f0", borderRadius:"4px", overflow:"hidden", boxShadow:"inset 0 1px 2px rgba(0,0,0,0.08)" }}>
          <div style={{ width:`${Math.min(valor,100)}%`, height:"100%", background:getGrad(valor), borderRadius:"4px", transition:"width 0.4s ease", boxShadow:`0 1px 3px ${col}60` }} />
        </div>
      </div>
    );
  };

  // Valor a mostrar: pctMeta si existe, sino pct
  const valorBarra = pctMeta != null ? pctMeta : pct;
  const labelBarra = metaLabel || label1 || "% Cumpl.";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"5px", minWidth:"160px" }}>
      <Barra valor={valorBarra} label={labelBarra} altura="8px" />
      {/* Segunda barra opcional: % incluyendo certificaciones de backlog —
          mismo criterio que Gráfica 1 (solo meta del mes) vs Gráfica 2
          (meta + backlog), para que Tabla 1 muestre la misma diferencia. */}
      {pctConBacklog != null && (
        <Barra valor={pctConBacklog} label={backlogLabel || "% Con backlog"} altura="6px" />
      )}
    </div>
  );
}

// ── Filtro de multiselección estilo Excel ──
// Botón que muestra un resumen de lo seleccionado y, al hacer clic, abre un
// panel con casillas (checkboxes) para elegir varios valores a la vez.
function MultiSelectDropdown({ label, placeholderTodos, options, selected, onChange }) {
  const [abierto, setAbierto]   = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClickFuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", onClickFuera);
    return () => document.removeEventListener("mousedown", onClickFuera);
  }, []);

  const activo = selected.length > 0;
  const opcionesFiltradas = busqueda
    ? options.filter(o => o.toLowerCase().includes(busqueda.toLowerCase()))
    : options;

  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };

  let resumen = placeholderTodos;
  if (selected.length === 1) resumen = selected[0];
  else if (selected.length > 1) resumen = `${selected.length} seleccionados`;

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <button
        type="button"
        onClick={() => setAbierto(a => !a)}
        title={selected.length > 1 ? selected.join(", ") : undefined}
        style={{
          padding:"7px 12px", border:`1.5px solid ${activo?CLARO_RED:"#e2e8f0"}`, borderRadius:"8px",
          fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer",
          fontWeight:activo?"700":"400", display:"flex", alignItems:"center", gap:"6px",
          maxWidth:"220px",
        }}>
        <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{resumen}</span>
        <span style={{ fontSize:"9px", color:CLARO_MUTED }}>▾</span>
      </button>
      {abierto && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:30,
          background:"white", border:"1px solid #e2e8f0", borderRadius:"10px",
          boxShadow:"0 6px 20px rgba(0,0,0,0.12)", minWidth:"220px", maxWidth:"280px",
          padding:"8px", display:"flex", flexDirection:"column", gap:"6px",
        }}>
          <input
            type="text" placeholder="Buscar..." value={busqueda} onChange={e=>setBusqueda(e.target.value)}
            style={{ padding:"6px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px", outline:"none" }}
            onClick={e=>e.stopPropagation()}
          />
          <div style={{ display:"flex", gap:"8px", padding:"2px 2px 4px", borderBottom:"1px solid #f1f5f9" }}>
            <button type="button" onClick={()=>onChange(options)}
              style={{ fontSize:"11px", color:CLARO_RED, background:"none", border:"none", cursor:"pointer", padding:0, fontWeight:"600" }}>
              Seleccionar todos
            </button>
            <button type="button" onClick={()=>onChange([])}
              style={{ fontSize:"11px", color:CLARO_MUTED, background:"none", border:"none", cursor:"pointer", padding:0, fontWeight:"600" }}>
              Limpiar
            </button>
          </div>
          <div style={{ maxHeight:"220px", overflowY:"auto", display:"flex", flexDirection:"column", gap:"2px" }}>
            {opcionesFiltradas.length === 0 && (
              <div style={{ fontSize:"11px", color:CLARO_MUTED, padding:"6px 4px" }}>Sin resultados</div>
            )}
            {opcionesFiltradas.map(opt => (
              <label key={opt} style={{ display:"flex", alignItems:"center", gap:"8px", fontSize:"12px", color:CLARO_DARK, padding:"4px", borderRadius:"6px", cursor:"pointer" }}>
                <input type="checkbox" checked={selected.includes(opt)} onChange={()=>toggle(opt)} style={{ cursor:"pointer" }} />
                <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PriorizacionHuella({ usuario }) {
  const [datos, setDatos]       = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState(null);

  // Área real (en px) del plot de cada gráfica, medida por GraficaChart y
  // reportada vía onArea — se usa para que la tabla de resumen de abajo
  // quede pixel a pixel alineada con los meses de su propia gráfica.
  const [areaG1, setAreaG1] = useState(null);
  const [areaG2, setAreaG2] = useState(null);

  // Filtros globales — afectan tarjetas y gráficas. Cada uno es un array
  // (multiselección estilo Excel): [] significa "sin filtro / todos".
  const [filtroAliado,    setFiltroAliado]    = useState([]);
  const [filtroJefe,      setFiltroJefe]      = useState([]);
  const [filtroCiudad,    setFiltroCiudad]    = useState([]);
  const [filtroPrioridad, setFiltroPrioridad] = useState([]);

  // Filtros tabla detalle
  const [filtroMes, setFiltroMes]       = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda]         = useState("");
  const [pagina, setPagina]             = useState(1);
  const POR_PAG = 12;

  // ── Fetch: datos FILTRADOS por aliado/jefe/ciudad/prioridad — alimentan
  // tanto las tarjetas de "Resumen general" como las Gráficas 1 y 2. ──
  useEffect(() => {
    const cargar = async () => {
      setCargando(true); setError(null);
      try {
        const params = new URLSearchParams();
        if (filtroAliado.length)    params.append("aliado",    filtroAliado.join(","));
        if (filtroJefe.length)      params.append("jefe",      filtroJefe.join(","));
        if (filtroCiudad.length)    params.append("ciudad",    filtroCiudad.join(","));
        if (filtroPrioridad.length) params.append("prioridad", filtroPrioridad.join(","));
        const res = await fetch(`${process.env.REACT_APP_API_URL}/priorizacion-huella?${params}`);
        if (!res.ok) throw new Error("Error al obtener datos del servidor");
        setDatos(await res.json());
      } catch (e) { setError(e.message); }
      finally { setCargando(false); }
    };
    cargar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroAliado, filtroJefe, filtroCiudad, filtroPrioridad]);

  const nodos      = datos?.nodos      || [];
  const resumenMes = datos?.resumenMes || [];   // FILTRADO — tarjetas y gráficas
  const metas      = datos?.metas      || [];   // FILTRADO — tarjetas y gráficas

  // Opciones para los dropdowns — EN CASCADA: el backend ya las calcula
  // teniendo en cuenta los demás filtros activos (p. ej. si filtraste por
  // aliado, acá solo llegan las ciudades/jefes que de verdad existen para
  // ese aliado).
  const opAliados     = datos?.aliados     || [];
  const opJefes       = datos?.jefes       || [];
  const opCiudades    = datos?.ciudades    || [];
  // Normalizar prioridades: deduplicar ignorando mayúsculas/minúsculas
  const opPrioridades = [...new Set(
    (datos?.prioridades || [])
      .filter(Boolean)
      .map(p => p.trim().toUpperCase())
  )].sort();
  // Nota: total_cert_otro_mes viene del backend para coincidir con las tablas
  const hayFiltro     = filtroAliado.length>0 || filtroJefe.length>0 || filtroCiudad.length>0 || filtroPrioridad.length>0;

  // Si al cambiar un filtro las opciones en cascada de OTRO dropdown se
  // achican (p. ej. cambiaste de aliado y la ciudad que tenías marcada ya
  // no aplica a ese aliado), limpiamos esa selección "fantasma" para que
  // nunca quede un filtro activo que ya no corresponde a nada visible.
  useEffect(() => {
    if (!datos) return;
    setFiltroAliado(prev => { const n = prev.filter(v => opAliados.includes(v)); return n.length === prev.length ? prev : n; });
    setFiltroJefe(prev => { const n = prev.filter(v => opJefes.includes(v)); return n.length === prev.length ? prev : n; });
    setFiltroCiudad(prev => { const n = prev.filter(v => opCiudades.includes(v)); return n.length === prev.length ? prev : n; });
    setFiltroPrioridad(prev => { const n = prev.filter(v => opPrioridades.includes(v)); return n.length === prev.length ? prev : n; });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datos]);

  // ── Tarjetas de "Resumen general": SIEMPRE con datos FILTRADOS ──
  const totalPriorizados  = nodos.length;
  const totalCertificados = datos?.total_certificados || 0;
  const totalPendientes    = nodos.filter(n => n.estado === "Pendiente").length;
  const totalSinEntrega    = nodos.filter(n => n.estado === "Sin entrega").length;
  const totalReprogramados = nodos.filter(n => n.estado === "Reprogramado").length;
  // Meta filtrada: el backend ya retorna solo la meta del aliado si aplica
  const metaAcumulada     = metas.reduce((s, m) => s + (Number(m.meta_total) || 0), 0);
  const certMetaFechaFiltrado = resumenMes.map(r => Number(r.cert_meta_fecha) || 0);
  const certBacklogFiltrado   = resumenMes.map(r => Number(r.cert_backlog)    || 0);
  // Cert. mismo mes = suma de cert_meta_fecha de todos los meses (Gráfica 1)
  const totalCertMismoMes = certMetaFechaFiltrado.reduce((s, v) => s + (v || 0), 0);
  // Cert. backlog = suma de certBacklog de todos los meses (Gráfica 2, naranja)
  const totalCertOtroMes  = certBacklogFiltrado.reduce((s, v) => s + (v || 0), 0);
  const pctAvanceGlobal   = metaAcumulada > 0
    ? Math.round((totalCertMismoMes + totalCertOtroMes) / metaAcumulada * 100) : 0;

  // ── Datos para las Gráficas 1 y 2 (y para Tabla 1 / Tabla 2 más abajo):
  // AHORA SÍ respetan el filtro de arriba (misma fuente que las tarjetas). ──
  const mesesKeys        = resumenMes.map(r => r.mes);
  const mesesLabel       = mesesKeys.map(k => MESES_LABEL[k] || k);
  const dataPrio         = resumenMes.map(r => Number(r.priorizados)     || 0);
  const certMetaFecha    = resumenMes.map(r => Number(r.cert_meta_fecha) || 0);
  const certBacklog      = resumenMes.map(r => Number(r.cert_backlog)    || 0);
  const totalFecha       = certMetaFecha.map((c, i) => c + certBacklog[i]);
  const dataPendientes   = resumenMes.map(r => Number(r.pendientes)   || 0);
  const dataSinEntrega   = resumenMes.map(r => Number(r.sin_entrega)  || 0);
  const dataReprogramados = resumenMes.map(r => Number(r.reprogramados) || 0);

  // Meta dinámica por mes (respeta el filtro de arriba)
  const metaMap    = Object.fromEntries(metas.map(m => [m.mes, Number(m.meta_total) || META]));
  const metaPorMes = mesesKeys.map(k => metaMap[k] || META);

  const pctG1 = certMetaFecha.map((c, i) => Math.round(c / metaPorMes[i] * 100));
  const pctG2 = totalFecha.map((c, i)    => Math.round(c / metaPorMes[i] * 100));
  const colG1 = pctG1.map(p => p >= 100 ? VERDE : ROJO);
  const colG2 = pctG2.map(p => p >= 100 ? VERDE : ROJO);

  const mesActualIdx  = mesesKeys.length - 1;
  const mesActualKey  = mesesKeys[mesActualIdx];
  const diasMesActual = DIAS_MES[mesActualKey] || 30;
  const diaActual     = new Date().getDate();
  const metaMesActual = metaPorMes[mesActualIdx] || META;

  const ritmo         = totalFecha[mesActualIdx] / diaActual;
  const proyNodos     = Math.round(ritmo * diasMesActual);
  const proyPct       = Math.round(proyNodos / metaMesActual * 100);
  const colorProy     = proyPct >= 100 ? VERDE : ROJO;
  const proyData      = mesesKeys.map((_, i) => i === mesActualIdx ? proyPct : null);

  const ritmoG1       = certMetaFecha[mesActualIdx] / diaActual;
  const proyNodosG1   = Math.round(ritmoG1 * diasMesActual);
  const proyPctG1     = Math.round(proyNodosG1 / metaMesActual * 100);
  const colorProyG1   = proyPctG1 >= 100 ? VERDE : ROJO;
  const proyDataG1    = mesesKeys.map((_, i) => i === mesActualIdx ? proyPctG1 : null);

  // Filtros de detalle manejados por TablaDetalle

  if (cargando) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"300px", flexDirection:"column", gap:"16px" }}>
      <div style={{ width:"36px", height:"36px", border:"3px solid #e2e8f0", borderTop:`3px solid ${CLARO_RED}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
      <span style={{ color:CLARO_MUTED, fontSize:"14px" }}>Cargando datos de priorización...</span>
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:"12px", padding:"24px", color:"#991b1b", fontSize:"14px" }}>
      ⚠️ Error al cargar los datos: {error}
    </div>
  );

  return (
    <div style={{ fontFamily:"'Segoe UI', Arial, sans-serif", color:CLARO_DARK }}>
      {/* ── Título ── */}
      <div style={{ marginBottom:"16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"4px" }}>
          <div style={{ width:"4px", height:"22px", background:CLARO_RED, borderRadius:"2px" }} />
          <h2 style={{ margin:0, fontSize:"20px", fontWeight:"700", color:CLARO_DARK }}>Dashboard Proyecto Huella</h2>
        </div>
        <p style={{ margin:"0 0 0 14px", fontSize:"13px", color:CLARO_MUTED }}>Seguimiento de priorización, certificación y cumplimiento de metas</p>
      </div>

      {/* ── Filtros globales ── */}
      <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"14px 20px", marginBottom:"14px" }}>
        <div style={{ fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:".06em", marginBottom:"10px" }}>
          🔽 Filtros globales — afectan tarjetas y gráficas
        </div>
        <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center" }}>
          <MultiSelectDropdown
            placeholderTodos="Todos los aliados"
            options={opAliados}
            selected={filtroAliado}
            onChange={setFiltroAliado}
          />
          <MultiSelectDropdown
            placeholderTodos="Todos los jefes de zona"
            options={opJefes}
            selected={filtroJefe}
            onChange={setFiltroJefe}
          />
          <MultiSelectDropdown
            placeholderTodos="Todas las ciudades"
            options={opCiudades}
            selected={filtroCiudad}
            onChange={setFiltroCiudad}
          />
          {hayFiltro && (
            <button
              onClick={()=>{ setFiltroAliado([]); setFiltroJefe([]); setFiltroCiudad([]); setFiltroPrioridad([]); }}
              style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:"#f8fafc", cursor:"pointer", color:CLARO_RED }}>
              ✕ Limpiar filtros
            </button>
          )}
          {hayFiltro && (
            <span style={{ fontSize:"11px", color:CLARO_RED, fontWeight:"600" }}>⚡ {totalPriorizados} nodos filtrados</span>
          )}
        </div>
      </div>

      {/* ── KPIs estilo tablas ── */}
      <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"16px 20px", marginBottom:"20px" }}>
        <div style={{ fontSize:"10px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:".06em", marginBottom:"12px" }}>
          Resumen general {hayFiltro && <span style={{ color:CLARO_RED }}>(filtrado)</span>}
        </div>
        <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center" }}>
          {[
            { label:"🎯 Priorizados",    valor:totalPriorizados,                    color:CLARO_DARK,  sub:hayFiltro?"Filtrado":(mesesKeys.length > 0 ? `${getMesCorto(mesesKeys[0])} – ${getMesCorto(mesesKeys[mesesKeys.length-1])} ${mesesKeys[mesesKeys.length-1]?.split("-")[0]||""}` : ""), desc:"Total nodos asignados" },
            { label:"✅ Cert. mismo mes", valor:totalCertMismoMes,                  color:VERDE,       sub:`${pct(totalCertMismoMes,metaAcumulada)}% vs meta`, desc:"Certificados en su mes de priorización (Gráfica 1)" },
            { label:"↗ Cert. Backlog",  valor:totalCertOtroMes,                   color:"#2563eb",   sub:`${pct(totalCertOtroMes,metaAcumulada)}% vs meta`,  desc:"Priorizados en mes anterior, certificados después (Gráfica 2)" },
            { label:"📦 Total ejecutado", valor:totalCertMismoMes + totalCertOtroMes, color:"#0891b2",   sub:`${pct(totalCertMismoMes+totalCertOtroMes,metaAcumulada)}% vs meta acum.`, desc:"Cert. mismo mes + backlog" },
            { label:"⏳ Pendientes",      valor:totalPendientes,                    color:"#7c3aed",   sub:"Mes actual",                                     desc:"Sin certificar este mes" },
            { label:"🔴 Bolsa Nodos",    valor:totalSinEntrega,                    color:NARANJ,      sub:"Meses anteriores",                               desc:"Priorizados sin certificar de meses anteriores" },
          ].map(({ label, valor, color, sub, desc }) => (
            <div key={label} style={{ background:"white", borderRadius:"10px", padding:"10px 16px", textAlign:"center", flex:"1 1 0", minWidth:"110px", maxWidth:"180px", border:"1px solid #e2e8f0", borderTop:`3px solid ${color}`, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize:"11px", fontWeight:"700", color:CLARO_DARK, marginBottom:"4px" }}>{label}</div>
              <div style={{ fontSize:"26px", fontWeight:"800", color, lineHeight:1 }}>{fmt(valor)}</div>
              {desc && <div style={{ fontSize:"9px", color:CLARO_MUTED, marginTop:"4px", lineHeight:"1.3" }}>{desc}</div>}
              {sub && <div style={{ fontSize:"9px", color:CLARO_LIGHT, marginTop:"2px" }}>{sub}</div>}
            </div>
          ))}

        </div>
      </div>

      <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px", marginBottom:"20px" }}>
        <div style={{ marginBottom:"12px" }}>
          <div style={{ fontSize:"13px", fontWeight:"700", color:CLARO_DARK }}>
            Gráfica 1 — Priorizados vs Meta vs Certificados en el mismo mes
          </div>
          <div style={{ fontSize:"11px", color:CLARO_MUTED, marginTop:"2px", lineHeight:"1.5" }}>
            Compara los nodos priorizados ese mes, la meta fija y los que se certificaron dentro del mes de priorización. El backlog no se incluye aquí.
          </div>
        </div>
        <Leyenda items={[
          { color:"#cbd5e1", label:"Priorizados ese mes" },
          { color:"#e2e8f0", label:`Meta (${metaPorMes[mesActualIdx] || META})` },
          { color:ROJO,      label:"Cert. mismo mes" },
          { color:"#1f2937", label:"% Cumplimiento", line:true },
          { color:ROJO,      label:"% Proyección", line:true, dash:true },
        ]} />
        <div>
          {/* La gráfica se envuelve en un contenedor GRAFICA_TOTAL_COL px más
              angosto que el ancho completo, porque la tabla de abajo tiene
              esa misma cantidad de px extra ocupados por la columna "Total"
              que la gráfica no tiene. Así, el área ejes+meses mide lo mismo
              en los dos y las columnas quedan alineadas pixel a pixel. */}
          <div style={{ width:`calc(100% - ${GRAFICA_TOTAL_COL}px)` }}>
            <GraficaChart
              datos={mesesKeys.map((k,i) => ({ mes:MESES_LABEL[k]||k, prio:dataPrio[i]||0, metaLine:metaPorMes[i]||META, cert:certMetaFecha[i]||0, meta:metaPorMes[i]||META, pct:pctG1[i]||0, proy:i===mesActualIdx?proyPctG1:null }))}
              colCert={colG1} colProy={colorProyG1} showBacklog={false}
              onArea={(a) => setAreaG1(prev => (prev && prev.left===a.left && prev.right===a.right) ? prev : a)}
            />
          </div>
          <TablaResumenHorizontal
            mesesKeys={mesesKeys} certArr={certMetaFecha} backArr={null}
            pctArr={pctG1} colArr={colG1} showProy={true}
            proyPct={proyPctG1} proyN={proyNodosG1} colorProy={colorProyG1}
            metaPorMes={metaPorMes} prioArr={dataPrio}
            metaArr={null}
            gutterIzq={areaG1 && areaG1.left} gutterDer={areaG1 && areaG1.right}
          />
        </div>
      </div>

      <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px", marginBottom:"24px" }}>
        <div style={{ fontSize:"13px", fontWeight:"700", color:CLARO_DARK, marginBottom:"2px" }}>
          Gráfica 2 — Total de certificaciones ejecutadas en el mes (meta + backlog)
        </div>
        <div style={{ fontSize:"11px", color:CLARO_MUTED, marginBottom:"12px", lineHeight:"1.5" }}>
          Todos los nodos certificados en el mes por <strong>fecha de certificación</strong>. <span style={{ color:ROJO, fontWeight:600 }}>Rojo</span> = son de la meta del mes. <span style={{ color:NARANJ, fontWeight:600 }}>Naranja</span> = backlog (venían de otro mes).
        </div>
        <Leyenda items={[
          { color:CLARO_RED,  label:`Meta del mes`, line:true, dash:false },
          { color:ROJO,       label:"Cert. meta del mes" },
          { color:NARANJ,     label:"Backlog (otros meses)" },
          { color:"#9ca3af",  label:`Meta (${metaPorMes[mesActualIdx] || META})`, line:true, dash:true },
          { color:"#1f2937",  label:"% Total ejecutado", line:true },
          { color:ROJO,       label:"% Proyección", line:true, dash:true },
        ]} />
        <div>
          {/* Mismo motivo que en Gráfica 1: ancho reducido en GRAFICA_TOTAL_COL
              px para que coincida con la zona ejes+meses de la tabla. */}
          <div style={{ width:`calc(100% - ${GRAFICA_TOTAL_COL}px)` }}>
            <GraficaChart
              datos={mesesKeys.map((k,i) => ({ mes:MESES_LABEL[k]||k, cert:certMetaFecha[i]||0, back:certBacklog[i]||0, meta:metaPorMes[i]||META, pct:pctG2[i]||0, proy:i===mesActualIdx?proyPct:null }))}
              colCert={colG2} colProy={colorProy} showBacklog={true} hidePrio={true}
              onArea={(a) => setAreaG2(prev => (prev && prev.left===a.left && prev.right===a.right) ? prev : a)}
            />
          </div>
          <TablaResumenHorizontal
            mesesKeys={mesesKeys} certArr={certMetaFecha} backArr={certBacklog}
            pctArr={pctG2} colArr={colG2} pctMetaArr={pctG1} showProy={true}
            proyPct={proyPct} proyN={proyNodos} colorProy={colorProy}
            proyPctMeta={proyPctG1}
            showTotal={true} metaPorMes={metaPorMes}
            gutterIzq={areaG2 && areaG2.left} gutterDer={areaG2 && areaG2.right}
          />
        </div>
      </div>

      <TablaAliado mesesLabel={Object.fromEntries(mesesKeys.map(k => [k, getMesLabel(k)]))} certBacklogArr={certBacklog} mesesKeys={mesesKeys} mesActual={mesActualKey} />
      <TablaJefe   mesesLabel={Object.fromEntries(mesesKeys.map(k => [k, getMesLabel(k)]))} certBacklogArr={certBacklog} mesesKeys={mesesKeys} mesActual={mesActualKey} />
      <TablaResumenTotal />
    </div>
  );
}

// ── Tabla 1: Aliado → Ciudad → Nodos ─────────────────────────
// (La meta ya no es un valor fijo — se trae en vivo de bd_metas_huella,
// ver `meta` en cada fila que entrega el backend.)

function TablaAliado({ mesesLabel, certBacklogArr, mesesKeys: mesesKeysProp, mesActual }) {
  const [datos, setDatos]               = useState([]);
  const [cargando, setCargando]         = useState(true);
  const [filtroMes, setFiltroMes]       = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroAliado, setFiltroAliado] = useState("");
  const [busqueda, setBusqueda]         = useState("");
  const [filtroKpi, setFiltroKpi]       = useState(null);
  const [expAliado, setExpAliado]       = useState({});
  const [expCiudad, setExpCiudad]       = useState({});

  // Cert. backlog del mes seleccionado (o suma total si no hay filtro)
  const certBacklogActual = (() => {
    if (!certBacklogArr || !mesesKeysProp) return 0;
    if (filtroMes === "todos") return certBacklogArr.reduce((s, v) => s + (v || 0), 0);
    if (filtroMes) {
      const idx = mesesKeysProp.indexOf(filtroMes);
      return idx >= 0 ? (certBacklogArr[idx] || 0) : 0;
    }
    const lastIdx = certBacklogArr.length - 1;
    return lastIdx >= 0 ? (certBacklogArr[lastIdx] || 0) : 0;
  })();

  const estadoStyle = (est) => {
    if (est === "Certificado")    return { bg:"#f0fdf4", color:VERDE,    border:"#16a34a", txt:"✓ Certificado"    };
    if (est === "Cert. otro mes") return { bg:"#eff6ff", color:"#2563eb",border:"white", txt:"↗ Cert. otro mes"  };
    if (est === "Sin entrega")    return { bg:"#fff7ed", color:NARANJ,   border:"#ea580c", txt:"⚠ Sin entrega"    };
    if (est === "Reprogramado")   return { bg:"#faf5ff", color:"#7c3aed", border:"#a78bfa", txt:"🔁 Reprogramado"  };
    return                               { bg:"#f8fafc", color:ROJO,     border:"#fca5a5", txt:"⏳ Pendiente"      };
  };

  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      try {
        const params = new URLSearchParams();
        if (filtroMes) params.append("mes", filtroMes);
        if (filtroEstado) params.append("estado", filtroEstado);
        const res  = await fetch(`${process.env.REACT_APP_API_URL}/priorizacion-huella-aliado?${params}`);
        const json = await res.json();
        setDatos(json);
      } catch { setDatos([]); }
      finally { setCargando(false); }
    };
    cargar();
  }, [filtroMes, filtroEstado]);

  const toggleAliado = (a)   => setExpAliado(e => ({ ...e, [a]: !e[a] }));
  const toggleCiudad = (key) => setExpCiudad(e => ({ ...e, [key]: !e[key] }));

  const q = busqueda.toLowerCase().trim();

  // Nota: "Certificados Backlog" NO se filtra aquí por estado — se filtra
  // por el flag real es_backlog_activo (ver paso 1 del filtrado abajo),
  // porque es un concepto distinto: nodos certificados ESTE mes pero cuya
  // priorización es de un mes anterior (no son parte de la meta oficial
  // del mes actual, que es "Certificado" = priorizado y certificado en el
  // mismo mes).
  const kpiEstados = {
    "Certificados":      ["Certificado"],
    "Cert. otro mes":    ["Cert. otro mes"],
    "Sin entrega":       ["Sin entrega"],
    "Pendientes":        ["Pendiente"],
    "Reprogramado":      ["Reprogramado"],
    "No entregados mes": ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"],
  };

  // Lista de aliados disponibles para el filtro, tomada de los datos reales
  const aliadosDisponibles = Array.from(new Set(datos.map(r => r.aliado).filter(Boolean))).sort();

  // En "Todos los meses" un mismo nodo puede aparecer varias veces — una
  // vez por cada ciclo de priorización (si se reprogramó, p. ej. de agosto
  // a septiembre). Cada fila sigue siendo real y se ve completa en el
  // detalle (así se entiende la historia del nodo), pero para que los
  // TOTALES no cuenten ese nodo más de una vez, aquí nos quedamos con un
  // solo registro por nodo: el de su ciclo más reciente (su estado final).
  // Así, un nodo reprogramado de agosto a septiembre y certificado en
  // septiembre, cuenta como 1 solo priorizado — el de septiembre —, no 2.
  const dedupePorNodoFinal = (nodos) => {
    const porNodo = {};
    nodos.forEach(n => {
      const actual = porNodo[n.id_nodo];
      if (!actual || (n.mes_priorizacion || "") > (actual.mes_priorizacion || "")) {
        porNodo[n.id_nodo] = n;
      }
    });
    return Object.values(porNodo);
  };

  // Mes "objetivo" del filtro actual (el mes cuya meta oficial se está
  // viendo): el mes específico elegido, o el mes actual si no hay filtro.
  // En "todos" no hay un único mes objetivo, así que no se usa para
  // restringir nada. El backend ahora trae, además de los nodos
  // PRIORIZADOS en el mes filtrado, los nodos CERTIFICADOS ese mes aunque
  // su priorización sea de un mes anterior (backlog) — para que
  // "Priorizados" siga representando solo la meta oficial del mes (y no se
  // infle con esos nodos de backlog), lo calculamos filtrando por este mes
  // objetivo en vez de contar todas las filas que llegaron del backend.
  const mesObjetivo = (filtroMes && filtroMes !== "todos") ? filtroMes : mesActual;

  // ── FILTRADO con recálculo de subtotales ───────────────────
  const filtrados = (filtroAliado ? datos.filter(r => r.aliado === filtroAliado) : datos).map(r => {
    // 1. Filtro KPI sobre nodos
    let ciudadesBase = r.ciudades || [];
    if (filtroKpi === "Certificados Backlog") {
      // Filtro especial: no es por estado, es por el flag real de backlog
      ciudadesBase = ciudadesBase.map(c => {
        const nodosFilt = (c.nodos || []).filter(n => n.es_backlog_activo === 1);
        return nodosFilt.length ? { ...c, nodos: nodosFilt } : null;
      }).filter(Boolean);
      if (!ciudadesBase.length) return null;
    } else if (filtroKpi && kpiEstados[filtroKpi]) {
      const estadosValidos = kpiEstados[filtroKpi];
      ciudadesBase = ciudadesBase.map(c => {
        const nodosFilt = (c.nodos || []).filter(n =>
          estadosValidos.includes(n.estado)
        );
        return nodosFilt.length ? { ...c, nodos: nodosFilt } : null;
      }).filter(Boolean);
      if (!ciudadesBase.length) return null;
    }

    // 2. Filtro búsqueda sobre nodos/ciudades
    if (q) {
      const aliadoCoinc = r.aliado?.toLowerCase().includes(q);

      if (aliadoCoinc) {
        // El aliado coincide → mostrar todas sus ciudades y nodos sin filtrar
        ciudadesBase = r.ciudades || [];
      } else {
        // El aliado no coincide → filtrar por ciudad o nodo
        ciudadesBase = ciudadesBase.map(c => {
          const ciudadCoinc = c.ciudad?.toLowerCase().includes(q);
          if (ciudadCoinc) return c; // ciudad coincide → todos sus nodos
          const nodosFiltrados = (c.nodos || []).filter(n =>
            n.id_nodo?.toLowerCase().includes(q) ||
            n.nombre_nodo?.toLowerCase().includes(q)
          );
          if (!nodosFiltrados.length) return null;
          return { ...c, nodos: nodosFiltrados };
        }).filter(Boolean);

        if (!ciudadesBase.length) return null;
      }
    }

    // 3. Recalcular subtotales de ciudad desde nodos reales
    //
    // El backend ahora trae, además de los nodos priorizados el mes
    // filtrado, los certificados ese mes aunque su priorización sea de un
    // mes anterior (backlog). Para que "Priorizados", "Certificados",
    // "Pendientes", etc. sigan representando SOLO la meta oficial del mes
    // (y no se inflen con esos nodos extra), todas esas métricas se
    // calculan sobre nodosDelMes (su propia priorización = mes objetivo).
    // El backlog, en cambio, se calcula sobre TODOS los nodos que llegaron
    // (nodos), porque es precisamente lo que queda FUERA de nodosDelMes.
    const ciudadesRecalc = ciudadesBase.map(c => {
      const nodos = c.nodos || [];
      const nodosDelMes = filtroMes === "todos" ? dedupePorNodoFinal(nodos) : nodos.filter(n => n.mes_priorizacion === mesObjetivo);
      const cert  = nodosDelMes.filter(n => n.estado === "Certificado").length;
      const otro  = nodosDelMes.filter(n => n.estado === "Cert. otro mes").length;
      const pend  = nodosDelMes.filter(n => n.estado === "Pendiente").length;
      const se    = nodosDelMes.filter(n => n.estado === "Sin entrega").length;
      const repr  = nodosDelMes.filter(n => n.estado === "Reprogramado").length;
      const back  = nodos.filter(n => n.es_backlog_activo === 1).length;
      const falt  = nodosDelMes.filter(n => ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"].includes(n.estado)).length;
      const prio  = nodosDelMes.length;
      const pctAv = prio > 0 ? Math.round((cert + otro) / prio * 100) : 0;
      const hhppC = nodosDelMes.reduce((s, n) => s + (Number(n.hhpp) || 0), 0);
      return { ...c, priorizados: prio, certificados: cert, cert_otro_mes: otro, pendientes: pend, sin_entrega: se, cert_backlog_activo: back, faltantes: falt, pct_avance: pctAv, hhpp: hhppC };
    });

    // 4. Recalcular subtotales de aliado desde ciudades recalculadas
    // (misma lógica: todo sobre nodosDelMes, backlog sobre todos los nodos)
    const nodosAliado = ciudadesBase.flatMap(c => c.nodos || []);
    const aNodosDelMes = filtroMes === "todos" ? dedupePorNodoFinal(nodosAliado) : nodosAliado.filter(n => n.mes_priorizacion === mesObjetivo);
    const aCert  = aNodosDelMes.filter(n => n.estado === "Certificado").length;
    const aOtro  = aNodosDelMes.filter(n => n.estado === "Cert. otro mes").length;
    const aPend  = aNodosDelMes.filter(n => n.estado === "Pendiente").length;
    const aSe    = aNodosDelMes.filter(n => n.estado === "Sin entrega").length;
    const aRepr  = aNodosDelMes.filter(n => n.estado === "Reprogramado").length;
    const aBack  = nodosAliado.filter(n => n.es_backlog_activo === 1).length;
    const aFalt  = aNodosDelMes.filter(n => ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"].includes(n.estado)).length;
    const aPrio  = aNodosDelMes.length;
    const aPct   = aPrio > 0 ? Math.round((aCert + aOtro) / aPrio * 100) : 0;
    const aHhpp  = aNodosDelMes.reduce((s, n) => s + (Number(n.hhpp) || 0), 0);

    return {
      ...r,
      ciudades:            ciudadesRecalc,
      priorizados:         aPrio,
      certificados:        aCert,
      cert_otro_mes:       aOtro,
      pendientes:          aPend,
      sin_entrega:         aSe,
      cert_backlog_activo: aBack,
      faltantes:           aFalt,
      pct_avance:          aPct,
      hhpp:                aHhpp,
    };
  }).filter(Boolean);

  // ── Totales globales desde nodos filtrados reales ──────────
  // Igual que en el paso 3/4: todo sobre el mes objetivo (la meta oficial),
  // el backlog sobre TODOS los nodos que llegaron (incluye los de fuera del
  // mes objetivo, que es justo lo que los hace "backlog").
  const todosNodosFiltrados = filtrados.flatMap(r =>
    (r.ciudades || []).flatMap(c => c.nodos || [])
  );
  const nodosDelMesGlobal = filtroMes === "todos" ? dedupePorNodoFinal(todosNodosFiltrados) : todosNodosFiltrados.filter(n => n.mes_priorizacion === mesObjetivo);
  const totGlobal = {
    prio: nodosDelMesGlobal.length,
    cert: nodosDelMesGlobal.filter(n => n.estado === "Certificado").length,
    back: nodosDelMesGlobal.filter(n => n.estado === "Cert. otro mes").length,
    otro: nodosDelMesGlobal.filter(n => n.estado === "Cert. otro mes").length,
    pend: nodosDelMesGlobal.filter(n => n.estado === "Pendiente").length,
    se:   nodosDelMesGlobal.filter(n => n.estado === "Sin entrega").length,
    repr: nodosDelMesGlobal.filter(n => n.estado === "Reprogramado").length,
    falt: nodosDelMesGlobal.filter(n => ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"].includes(n.estado)).length,
    hhpp: nodosDelMesGlobal.reduce((s, n) => s + (Number(n.hhpp) || 0), 0),
    // Certificados Backlog: certificados ESTE mes pero cuya priorización
    // viene de un mes anterior — distinto de "Certificado" (priorizado y
    // certificado en el mismo mes, la meta oficial). Se calcula sobre
    // TODOS los nodos filtrados, no solo los del mes objetivo.
    backlogReal: todosNodosFiltrados.filter(n => n.es_backlog_activo === 1).length,
  };
  totGlobal.pct = totGlobal.prio > 0
    ? Math.round((totGlobal.cert + totGlobal.otro) / totGlobal.prio * 100)
    : 0;

  // Meta REAL: viene del backend (tabla bd_metas_huella) ya sumada según
  // el filtro de mes aplicado (mes específico, "todos" o mes actual por
  // defecto) — así se respeta la meta real de cada mes aunque cambie
  // (p. ej. subió en sep/oct), en vez de asumir un valor fijo multiplicado
  // por la cantidad de meses.
  const metaFiltrada = filtrados.reduce((sum, r) => sum + (Number(r.meta) || 0), 0);

  const esMesActualCol = !filtroMes || filtroMes === mesActual;
  const todosMesesCol  = filtroMes === "todos";

  // Auto-expandir cuando hay búsqueda activa
  useEffect(() => {
    if (!q) return;
    const newExpA = {}, newExpC = {};
    filtrados.forEach(r => {
      newExpA[r.aliado] = true;
      r.ciudades?.forEach(c => {
        const ck = `${r.aliado}__${c.ciudad}`;
        if (c.nodos?.length > 0) newExpC[ck] = true;
      });
    });
    setExpAliado(newExpA);
    setExpCiudad(newExpC);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda]);

  const expandAll = () => {
    const a = {}, c = {};
    filtrados.forEach(r => {
      a[r.aliado] = true;
      r.ciudades?.forEach(ci => { c[`${r.aliado}__${ci.ciudad}`] = true; });
    });
    setExpAliado(a); setExpCiudad(c);
  };
  const collapseAll = () => { setExpAliado({}); setExpCiudad({}); };

  const descargarCSVT1 = () => {
    const enc = (v) => `"${String(v ?? "").replace(/"/g,'""')}"`;
    const cols = ["Aliado","Ciudad","Nodo","Mes Priorizacion","Cal. ICR","HHPP","Estado","Cert. Meta Mes","Cert. Backlog","OT / CHG","Fecha Cert."];
    const filas = [cols.join(";")];
    filtrados.forEach(aliado => {
      (aliado.ciudades || []).forEach(ciudad => {
        (ciudad.nodos || []).forEach(nodo => {
          filas.push([
            enc(aliado.aliado), enc(ciudad.ciudad), enc(nodo.id_nodo),
            enc(nodo.mes_priorizacion || ""),
            enc(nodo.priorizacion_calidad ?? ""), enc(nodo.hhpp ?? ""),
            enc(nodo.estado || ""),
            nodo.estado === "Certificado" ? 1 : 0,
            nodo.es_backlog_activo ? 1 : 0,
            enc(nodo.ot_chg || ""), enc(nodo.fecha_cert || "")
          ].join(";"));
        });
      });
    });
    const blob = new Blob(["\uFEFF" + filas.join("\n")], { type:"text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `T1_PriorizacionHuella_${filtroMes || "mes_actual"}.csv`;
    a.click();
  };

  const thSt = { padding:"8px 10px", textAlign:"left", fontWeight:"700", color:CLARO_MUTED, fontSize:"10px", textTransform:"uppercase", letterSpacing:"0.04em", borderBottom:"1px solid #e2e8f0", whiteSpace:"normal", lineHeight:"1.4", verticalAlign:"bottom", background:CLARO_BG };

  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px", marginBottom:"20px" }}>
      <div style={{ marginBottom:"14px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"3px" }}>
          <div style={{ width:"4px", height:"18px", background:CLARO_RED, borderRadius:"2px" }} />
          <div style={{ fontSize:"14px", fontWeight:"700", color:CLARO_DARK }}>
            Tabla 1 — Priorizados vs Certificados por Aliado y Ciudad
          </div>
        </div>
        <div style={{ fontSize:"11px", color:CLARO_MUTED, marginLeft:"12px" }}>
          Haz clic en ▶ para ver el detalle por ciudad y nodo. Las tarjetas de arriba filtran la tabla — clic para activar.
        </div>
      </div>

      {/* KPIs — ahora muestran totales de los nodos filtrados */}
      <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:"14px", alignItems:"center" }}>
        {(() => {
          const esMesActualT1 = !filtroMes || filtroMes === mesActual;
          const todosMesesT1  = filtroMes === "todos";
          return [
            { label:"🎯 Priorizados",         sub:"Total asignados",                       valor:totGlobal.prio, color:CLARO_DARK, kpi:null,             show:true },
            { label:"🏁 Meta",                sub: (metaFiltrada > 0 ? `${Math.round(totGlobal.cert / metaFiltrada * 100)}% cumplido — ` : "") + (todosMesesT1 ? "Meta acumulada" : "Meta del mes"),  valor:metaFiltrada, color:CLARO_RED, kpi:null, show:true },
            { label:"✅ Certificados",        sub:"Priorizados y certificados en el mismo mes (meta oficial)", valor:totGlobal.cert, color:VERDE,      kpi:"Certificados",    show:true },
            { label:"↗ Cert. otro mes",      sub:"Priorizados este mes, pero certificados en un mes diferente", valor:totGlobal.otro, color:"#2563eb", kpi:"Cert. otro mes", show:true },
            { label:"📦 Certificados Backlog", sub:"Certificados este mes, pero priorizados en un mes anterior (no son de la meta oficial)", valor:totGlobal.backlogReal, color:"#0891b2",  kpi:"Certificados Backlog",   show:true },
            { label:"⏳ Pendientes",          sub:"Sin cert. en mes actual",                valor:totGlobal.pend, color:"#7c3aed",  kpi:"Pendientes",      show:todosMesesT1 || esMesActualT1 },
            { label:"🔴 Bolsa Nodos",        sub:"Nodos Priorizados pero no Ejecutados y/o Entregados", valor:totGlobal.se, color:NARANJ, kpi:"Sin entrega", show:todosMesesT1 || !esMesActualT1 },
            { label:"🔁 Reprogramados",      sub:"Nodos de este mes reprogramados a un mes posterior", valor:totGlobal.repr, color:"#9333ea", kpi:"Reprogramado", show:true },
          ].filter(t => t.show);
        })().map(({ label, valor, color, kpi, sub }) => {
          const activo = filtroKpi === kpi && kpi !== null;
          return (
            <div key={label}
              onClick={() => { setFiltroKpi(activo ? null : kpi); setExpAliado({}); setExpCiudad({}); }}
              style={{
                background: activo ? color : "white",
                borderRadius:"10px", padding:"10px 16px", textAlign:"center", minWidth:"110px",
                cursor: kpi ? "pointer" : "default",
                border: activo ? `2px solid ${color}` : `1px solid #e2e8f0`,
                borderTop: activo ? `2px solid ${color}` : `3px solid ${color}`,
                transition:"all 0.15s",
                boxShadow: activo ? `0 2px 10px ${color}40` : "0 1px 3px rgba(0,0,0,0.06)",
              }}
              title={kpi ? `Clic para filtrar por: ${label}` : undefined}
            >
              <div style={{ fontSize:"12px", fontWeight:"700", color: activo ? "white" : CLARO_DARK, marginBottom:"4px" }}>{label}</div>
              <div style={{ fontSize:"26px", fontWeight:"800", color: activo ? "white" : color, lineHeight:1 }}>{fmt(valor)}</div>
              {sub && <div style={{ fontSize:"9px", color: activo ? "rgba(255,255,255,0.85)" : CLARO_MUTED, marginTop:"4px", lineHeight:"1.3" }}>{sub}</div>}
              {kpi && <div style={{ fontSize:"8px", color: activo ? "rgba(255,255,255,0.7)" : CLARO_LIGHT, marginTop:"3px" }}>{activo ? "✕ limpiar filtro" : "↑ clic para filtrar"}</div>}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize:"10.5px", color:CLARO_MUTED, marginBottom:"12px", lineHeight:"1.4" }}>
        🎯 Priorizados = ✅ Certificados + ↗ Cert. otro mes + 🔴 Bolsa Nodos + 🔁 Reprogramados (+ ⏳ Pendientes cuando aplica).
        📦 Certificados Backlog es aparte: cuenta nodos de <strong>otros</strong> meses certificados ahora, no es parte de los Priorizados de este mes.
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"12px", alignItems:"center" }}>
        {filtroKpi && (
          <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 10px", borderRadius:"8px", background:"#f3f0ff", border:"1px solid #c4b5fd", fontSize:"11px", color:"#7c3aed", fontWeight:"600" }}>
            Filtrando: {filtroKpi}
            <span onClick={()=>setFiltroKpi(null)} style={{ cursor:"pointer", color:"#7c3aed", fontWeight:"700", fontSize:"13px" }}>✕</span>
          </div>
        )}
        <input type="text" placeholder="🔍 Buscar aliado, ciudad o nodo..." value={busqueda}
          onChange={e=>setBusqueda(e.target.value)}
          style={{ padding:"7px 11px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, outline:"none", minWidth:"220px" }}
          onFocus={e=>e.target.style.borderColor=CLARO_RED}
          onBlur={e=>e.target.style.borderColor="#e2e8f0"} />
        <select value={filtroAliado} onChange={e=>{ setFiltroAliado(e.target.value); setExpAliado({}); setExpCiudad({}); }}
          style={{ padding:"7px 11px", border: filtroAliado ? `1.5px solid ${CLARO_RED}` : "1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Todos los aliados</option>
          {aliadosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtroMes} onChange={e=>setFiltroMes(e.target.value)}
          style={{ padding:"7px 11px", border: filtroMes === "todos" ? `1.5px solid #0891b2` : "1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Mes actual</option>
          <option value="todos">Todos los meses</option>
          {Object.entries(mesesLabel).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {/* Botón limpiar filtros — visible solo si hay algo activo */}
        {(busqueda || filtroMes || filtroEstado || filtroAliado || filtroKpi) && (
          <button
            onClick={() => { setBusqueda(""); setFiltroMes(""); setFiltroEstado(""); setFiltroAliado(""); setFiltroKpi(null); setExpAliado({}); setExpCiudad({}); }}
            style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:"#f8fafc", cursor:"pointer", color:CLARO_RED, display:"flex", alignItems:"center", gap:"5px" }}>
            ✕ Limpiar filtros
          </button>
        )}
        <button onClick={expandAll}
          style={{ padding:"7px 12px", borderRadius:"8px", fontSize:"11px", fontWeight:"600", border:"1px solid #e2e8f0", background:"white", cursor:"pointer", color:CLARO_MUTED }}>
          ↕ Expandir todo
        </button>
        <button onClick={collapseAll}
          style={{ padding:"7px 12px", borderRadius:"8px", fontSize:"11px", fontWeight:"600", border:"1px solid #e2e8f0", background:"white", cursor:"pointer", color:CLARO_MUTED }}>
          ↕ Colapsar todo
        </button>
        <button onClick={descargarCSVT1}
          style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:CLARO_RED, cursor:"pointer", color:"white", display:"flex", alignItems:"center", gap:"5px", marginLeft:"auto" }}>
          ⬇ Descargar CSV
        </button>
      </div>

      {filtroMes === "todos" && (
        <div style={{ background:"#ecfeff", border:"1px solid #a5f3fc", borderRadius:"8px", padding:"10px 14px", marginBottom:"14px", fontSize:"11.5px", color:"#0e7490", lineHeight:"1.5" }}>
          ℹ️ <strong>Viendo todos los meses:</strong> si un nodo fue reprogramado de un mes a otro, los totales (Priorizados, Certificados, Pendientes, etc.) lo cuentan <strong>una sola vez</strong>, con su estado más reciente — no se duplica entre el mes viejo y el nuevo.
          El detalle debajo de cada ciudad sí muestra el historial completo (todas sus filas, incluida la que dice "Reprogramado"), para que puedas ver por qué cambió de mes.
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign:"center", padding:"32px", color:CLARO_MUTED, fontSize:"13px" }}>Cargando...</div>
      ) : (
        <div style={{ overflowX:"auto", borderRadius:"8px", border:"1px solid #e2e8f0" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px", tableLayout:"fixed" }}>
            <thead>
              <tr>
                <th style={{...thSt, width:"16%"}}>Aliado / Ciudad / Nodo</th>
                <th style={{...thSt, width:"7%"}}>Mes prio.</th>
                <th style={{...thSt, width:"6%"}} title="Priorización de calidad ICR">Cal. ICR</th>
                <th style={{...thSt, width:"6%"}} title="Horas hombre programadas">HHPP</th>
                <th style={{...thSt, width:"5%"}} title="Total nodos asignados en el mes">Asignados</th>
                <th style={{...thSt, width:"6%", color:VERDE}} title="Priorizados y certificados en el mismo mes — la meta oficial del mes">✅ Cert.<br/>meta mes</th>
                <th style={{...thSt, width:"7%", color:"#0891b2"}} title="Certificados este mes, pero priorizados en un mes anterior — no son de la meta oficial del mes">📦 Cert.<br/>Backlog</th>
                <th style={{...thSt, width: todosMesesCol ? "13%" : "6%", color: esMesActualCol ? "#7c3aed" : NARANJ}} title={esMesActualCol ? "Sin certificar en el mes actual" : "Nodos de meses anteriores sin certificar"}>
                  {todosMesesCol ? "⏳ Pend. / 🔴 Bolsa" : esMesActualCol ? "⏳ Pendientes" : "🔴 Bolsa Nodos"}
                </th>
                <th style={{...thSt, width:"12%"}}>% Avance</th>
                <th style={{...thSt, width:"9%"}}>OT / CHG</th>
                <th style={{...thSt, width:"9%"}}>Fecha cert.</th>
                <th style={{...thSt, width:"10%"}}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr><td colSpan="13" style={{ padding:"28px", textAlign:"center", color:"#94a3b8" }}>Sin resultados</td></tr>
              ) : filtrados.map(aliadoRow => (
                <>
                  {/* ── Fila ALIADO ── */}
                  <tr key={aliadoRow.aliado}
                    onClick={() => toggleAliado(aliadoRow.aliado)}
                    style={{ background:"white", cursor:"pointer", borderLeft:"4px solid #E30613" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"}
                    onMouseLeave={e=>e.currentTarget.style.background="white"}>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_DARK }}>
                      <span style={{ marginRight:"8px", color:CLARO_RED }}>
                        {expAliado[aliadoRow.aliado] ? "▼" : "▶"}
                      </span>
                      {aliadoRow.aliado}
                      <span style={{ marginLeft:"8px", fontSize:"10px", color:"#9ca3af", fontWeight:"400" }}>
                        ({aliadoRow.ciudades?.length} ciudad{aliadoRow.ciudades?.length!==1?"es":""})
                      </span>
                    </td>
                    <td style={{ padding:"10px 14px", color:CLARO_MUTED }}></td>{/* Mes prio. vacío */}
                    <td style={{ padding:"10px 14px", color:CLARO_MUTED }}></td>{/* Cal.ICR vacío */}
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_MUTED, textAlign:"right", fontSize:"12px" }}>{aliadoRow.hhpp ? fmt(aliadoRow.hhpp) : "—"}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_DARK }}>{aliadoRow.priorizados}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:VERDE }}>{aliadoRow.certificados}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:"#0891b2" }}>{aliadoRow.cert_backlog_activo||0}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color: esMesActualCol ? "#7c3aed" : NARANJ }}>
                      {todosMesesCol ? `${aliadoRow.pendientes||0} / ${aliadoRow.sin_entrega||0}` : esMesActualCol ? (aliadoRow.pendientes||0) : (aliadoRow.sin_entrega||0)}
                    </td>
                    <td style={{ padding:"10px 14px" }}>
                      <BarraAvance
                        pct={aliadoRow.pct_avance}
                        pctMeta={(() => {
                          const meta = Number(aliadoRow.meta) || 0;
                          const certEfectivo = esMesActualCol
                            ? (aliadoRow.certificados + (aliadoRow.cert_otro_mes||0))
                            : (aliadoRow.priorizados - (aliadoRow.sin_entrega||0));
                          return meta > 0 ? Math.round(certEfectivo / meta * 100) : 0;
                        })()}
                        metaLabel={(() => {
                          const meta = Number(aliadoRow.meta) || 0;
                          return filtroMes === "todos" ? `vs meta acum. (${meta})` : `vs meta mes (${meta})`;
                        })()}
                        esMesActual={!filtroMes || filtroMes === mesActual}
                        bolsaCerrada={!!filtroMes && filtroMes !== mesActual}
                        sinEntrega={aliadoRow.sin_entrega}
                        pctConBacklog={(() => {
                          const meta = Number(aliadoRow.meta) || 0;
                          if (meta <= 0) return null;
                          const certEfectivo = esMesActualCol
                            ? (aliadoRow.certificados + (aliadoRow.cert_otro_mes||0))
                            : (aliadoRow.priorizados - (aliadoRow.sin_entrega||0));
                          return Math.round((certEfectivo + (aliadoRow.cert_backlog_activo||0)) / meta * 100);
                        })()}
                        backlogLabel="% Con backlog"
                      />
                    </td>
                    <td colSpan="3" style={{ padding:"10px 14px", color:"#6b7280", fontSize:"11px" }}></td>
                  </tr>

                  {/* ── Filas CIUDAD ── */}
                  {expAliado[aliadoRow.aliado] && aliadoRow.ciudades?.map(ciudadRow => {
                    const ck = `${aliadoRow.aliado}__${ciudadRow.ciudad}`;
                    return (
                      <>
                        <tr key={ck}
                          onClick={() => toggleCiudad(ck)}
                          style={{ background:"#f9fafb", cursor:"pointer", borderBottom:"1px solid #e5e7eb" }}
                          onMouseEnter={e=>e.currentTarget.style.background="#e2e8f0"}
                          onMouseLeave={e=>e.currentTarget.style.background="#f8fafc"}>
                          <td style={{ padding:"9px 14px 9px 32px", fontWeight:"600", color:CLARO_DARK }}>
                            <span style={{ marginRight:"8px", fontSize:"10px", color:"#6366f1" }}>
                              {expCiudad[ck] ? "▼" : "▶"}
                            </span>
                            📍 {ciudadRow.ciudad}
                            <span style={{ marginLeft:"8px", fontSize:"10px", color:CLARO_MUTED, fontWeight:"400" }}>
                              ({ciudadRow.nodos?.length} nodo{ciudadRow.nodos?.length!==1?"s":""})
                            </span>
                          </td>
                          <td style={{ padding:"9px 14px", color:CLARO_MUTED }}></td>{/* Mes prio. vacío */}
                          <td style={{ padding:"9px 14px", color:CLARO_MUTED }}></td>{/* Cal.ICR vacío */}
                          <td style={{ padding:"9px 14px", fontWeight:"600", color:CLARO_MUTED, textAlign:"right", fontSize:"11px" }}>{ciudadRow.hhpp ? fmt(ciudadRow.hhpp) : "—"}</td>
                          <td style={{ padding:"9px 14px", fontWeight:"600", color:CLARO_DARK }}>{ciudadRow.priorizados}</td>
                          <td style={{ padding:"9px 14px", fontWeight:"600", color:ciudadRow.certificados>0?VERDE:CLARO_MUTED }}>{ciudadRow.certificados}</td>
                          <td style={{ padding:"9px 14px", fontWeight:"600", color:ciudadRow.cert_backlog_activo>0?"#0891b2":CLARO_MUTED }}>{ciudadRow.cert_backlog_activo||0}</td>
                          <td style={{ padding:"9px 14px", fontWeight:"600", color: esMesActualCol ? (ciudadRow.pendientes>0?"#7c3aed":CLARO_MUTED) : (ciudadRow.sin_entrega>0?NARANJ:CLARO_MUTED) }}>
                            {todosMesesCol ? `${ciudadRow.pendientes||0} / ${ciudadRow.sin_entrega||0}` : esMesActualCol ? (ciudadRow.pendientes||0) : (ciudadRow.sin_entrega||0)}
                          </td>
                          <td style={{ padding:"9px 14px" }}>
                            <BarraAvance
                              pct={ciudadRow.priorizados > 0 ? Math.round(ciudadRow.certificados / ciudadRow.priorizados * 100) : 0}
                              pctMeta={ciudadRow.priorizados > 0 ? Math.round((esMesActualCol ? ciudadRow.certificados + (ciudadRow.cert_otro_mes||0) : ciudadRow.priorizados - (ciudadRow.sin_entrega||0)) / ciudadRow.priorizados * 100) : 0}
                              metaLabel="% Cumpl."
                              esMesActual={!filtroMes || filtroMes === mesActual}
                              bolsaCerrada={!!filtroMes && filtroMes !== mesActual}
                              sinEntrega={ciudadRow.sin_entrega}
                              soloPendientes={ciudadRow.certificados === 0 && ciudadRow.cert_otro_mes === 0 && ciudadRow.sin_entrega === 0 && ciudadRow.pendientes > 0}
                              pctConBacklog={ciudadRow.priorizados > 0 ? Math.round(((esMesActualCol ? ciudadRow.certificados + (ciudadRow.cert_otro_mes||0) : ciudadRow.priorizados - (ciudadRow.sin_entrega||0)) + (ciudadRow.cert_backlog_activo||0)) / ciudadRow.priorizados * 100) : null}
                              backlogLabel="% Con backlog"
                            />
                          </td>
                          <td colSpan="3" style={{ padding:"9px 14px" }}></td>
                        </tr>

                        {/* ── Filas NODO ── */}
                        {expCiudad[ck] && ciudadRow.nodos?.map((nodo, ni) => {
                          const es = estadoStyle(nodo.estado);
                          return (
                            <tr key={`${ck}-${ni}`}
                              style={{ borderBottom:"1px solid #f1f5f9" }}
                              onMouseEnter={e=>e.currentTarget.style.background="#fafafa"}
                              onMouseLeave={e=>e.currentTarget.style.background="white"}>
                              <td style={{ padding:"7px 14px 7px 52px", color:CLARO_DARK, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                <span style={{ color:"#cbd5e1", marginRight:"6px", fontSize:"11px" }}>└</span>
                                <span style={{ fontWeight:"700", color:CLARO_DARK, marginRight:"6px" }}>{nodo.id_nodo}</span>
                                <span style={{ color:CLARO_MUTED, fontSize:"11px" }}>
                                  {nodo.nombre_nodo !== nodo.id_nodo ? nodo.nombre_nodo : ""}
                                </span>
                                {nodo.estado === "Reprogramado" && nodo.veces_reprogramado > 0 && (
                                  <span title={`Reprogramado ${nodo.veces_reprogramado} vez(ces)`} style={{ marginLeft:"6px", display:"inline-flex", alignItems:"center", gap:"3px", background:"#faf5ff", color:"#7c3aed", border:"1px solid #a78bfa", borderRadius:"20px", fontSize:"10px", fontWeight:"700", padding:"1px 7px" }}>
                                    🔁 ×{nodo.veces_reprogramado}
                                  </span>
                                )}
                                {nodo.estado !== "Reprogramado" && nodo.veces_reprogramado > 0 && (
                                  <span title={`Fue reprogramado ${nodo.veces_reprogramado} vez(ces) antes de ejecutarse`} style={{ marginLeft:"6px", display:"inline-flex", alignItems:"center", gap:"3px", background:"#f5f3ff", color:"#9333ea", border:"1px solid #d8b4fe", borderRadius:"20px", fontSize:"10px", fontWeight:"600", padding:"1px 7px" }}>
                                    🔁 Reprogram. ×{nodo.veces_reprogramado}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding:"7px 14px", color:CLARO_MUTED, fontSize:"11px", whiteSpace:"nowrap" }}>
                                {mesMostrar(nodo.mes_priorizacion)}
                              </td>
                              <td style={{ padding:"7px 14px", fontSize:"11px", fontWeight:"600", color:CLARO_DARK, textAlign:"right" }}>
                                {nodo.priorizacion_calidad != null ? fmt(nodo.priorizacion_calidad) : "—"}
                              </td>
                              <td style={{ padding:"7px 14px", fontSize:"11px", color:CLARO_MUTED, textAlign:"right" }}>
                                {nodo.hhpp != null ? fmt(nodo.hhpp) : "—"}
                              </td>
                              <td style={{ padding:"7px 14px", fontSize:"11px", fontWeight:"600", color:CLARO_DARK }}>1</td>
                              <td style={{ padding:"7px 14px", fontSize:"11px", fontWeight:"600",
                                color: nodo.estado === "Certificado" ? VERDE : CLARO_MUTED }}>
                                {nodo.estado === "Certificado" ? "1" : "0"}
                              </td>
                              <td style={{ padding:"7px 14px", fontSize:"11px", fontWeight:"600", color: nodo.es_backlog_activo === 1 ? "#0891b2" : CLARO_MUTED }}>
                                {nodo.es_backlog_activo === 1 ? "1" : "0"}
                              </td>
                              <td style={{ padding:"7px 14px", fontSize:"11px", fontWeight:"600", color: esMesActualCol ? (nodo.estado==="Pendiente"?"#7c3aed":CLARO_MUTED) : (nodo.estado==="Sin entrega"?NARANJ:CLARO_MUTED) }}>
                                {todosMesesCol
                                  ? `${nodo.estado==="Pendiente"?"1":"0"} / ${nodo.estado==="Sin entrega"?"1":"0"}`
                                  : esMesActualCol ? (nodo.estado==="Pendiente"?"1":"0") : (nodo.estado==="Sin entrega"?"1":"0")}
                              </td>
                              <td style={{ padding:"7px 14px" }}>
                                <BarraAvance
                                  pct={["Certificado","Cert. otro mes","Reprogramado"].includes(nodo.estado) ? 100 : 0}
                                  etiqueta={
                                    nodo.estado === "Certificado"    ? "% Cumpl." :
                                    nodo.estado === "Cert. otro mes" ? "% Cumpl." :
                                    nodo.estado === "Reprogramado"   ? "% Cumpl." :
                                    nodo.estado === "Pendiente"      ? "% Cumpl.":
                                    "% Cumpl."
                                  }
                                />
                              </td>
                              <td style={{ padding:"7px 14px", fontFamily:"monospace", fontSize:"11px", color:CLARO_MUTED }}>
                                {nodo.ot_chg || "—"}
                              </td>
                              <td style={{ padding:"7px 14px", fontSize:"11px", whiteSpace:"nowrap" }}>
                                {nodo.fecha_cert
                                  ? <span style={{ color: nodo.estado === "Cert. otro mes" ? "#2563eb" : nodo.estado === "Reprogramado" ? "#7c3aed" : CLARO_MUTED, fontWeight: ["Cert. otro mes","Reprogramado"].includes(nodo.estado) ? "700" : "400" }}>
                                      {mesMostrar(nodo.fecha_cert)}
                                    </span>
                                  : "—"
                                }
                              </td>
                              <td style={{ padding:"7px 14px" }}>
                                <span style={{ display:"inline-block", fontSize:"10px", fontWeight:"700", padding:"2px 8px", borderRadius:"20px", background:es.bg, color:es.color, border:`1px solid ${es.border}` }}>
                                  {es.txt}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    );
                  })}
                </>
              ))}

              {/* Total global — siempre desde nodos filtrados */}
              <tr style={{ background:"#f3f4f6", borderTop:"2px solid #e5e7eb", borderLeft:"4px solid "+CLARO_RED }}>
                <td style={{ padding:"10px 14px", fontWeight:"700", fontSize:"12px", color:CLARO_DARK }}>TOTAL GENERAL</td>
                <td></td>{/* Mes prio. vacío */}
                <td></td>{/* Cal. ICR vacío */}
                <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_MUTED, textAlign:"right", fontSize:"12px" }}>{totGlobal.hhpp ? fmt(totGlobal.hhpp) : "—"}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_DARK }}>{totGlobal.prio}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color:VERDE }}>{totGlobal.cert}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color:"#0891b2" }}>{totGlobal.backlogReal}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color: esMesActualCol ? "#7c3aed" : NARANJ }}>
                  {todosMesesCol ? `${totGlobal.pend} / ${totGlobal.se}` : esMesActualCol ? totGlobal.pend : totGlobal.se}
                </td>
                <td style={{ padding:"10px 14px" }}>
                  <BarraAvance
                    pct={totGlobal.pct}
                    pctMeta={metaFiltrada > 0 ? Math.round(totGlobal.cert / metaFiltrada * 100) : 0}
                    metaLabel={filtroMes === "todos" ? `vs meta acum. (${metaFiltrada})` : `vs meta mes (${metaFiltrada})`}
                    esMesActual={!filtroMes || filtroMes === mesActual}
                    bolsaCerrada={!!filtroMes && filtroMes !== mesActual && filtroMes !== "todos"}
                    sinEntrega={totGlobal.se}
                    pctConBacklog={metaFiltrada > 0 ? Math.round((totGlobal.cert + totGlobal.backlogReal) / metaFiltrada * 100) : null}
                    backlogLabel="% Con backlog"
                  />
                </td>
                <td colSpan="3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tabla 2: Jefe Zona → Aliado → Ciudad → Nodo ──────────────
function TablaJefe({ mesesLabel, certBacklogArr, mesesKeys: mesesKeysProp, mesActual }) {
  const [datos, setDatos]         = useState([]);
  const [cargando, setCargando]   = useState(true);
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroJefe, setFiltroJefe] = useState("");
  const [busqueda, setBusqueda]   = useState("");
  const [filtroKpi, setFiltroKpi] = useState(null);
  const [expJefe, setExpJefe]     = useState({});
  const [expAliado, setExpAliado] = useState({});
  const [expCiudad, setExpCiudad] = useState({});

  // Cert. backlog del mes seleccionado (o suma total si no hay filtro)
  const certBacklogActual = (() => {
    if (!certBacklogArr || !mesesKeysProp) return 0;
    if (filtroMes === "todos") return certBacklogArr.reduce((s, v) => s + (v || 0), 0);
    if (filtroMes) {
      const idx = mesesKeysProp.indexOf(filtroMes);
      return idx >= 0 ? (certBacklogArr[idx] || 0) : 0;
    }
    const lastIdx = certBacklogArr.length - 1;
    return lastIdx >= 0 ? (certBacklogArr[lastIdx] || 0) : 0;
  })();

  const estadoStyle = (est) => {
    if (est === "Certificado")    return { bg:"#f0fdf4", color:VERDE,     border:"#16a34a", txt:"✓ Certificado"   };
    if (est === "Cert. otro mes") return { bg:"#eff6ff", color:"#2563eb", border:"white", txt:"↗ Cert. otro mes" };
    if (est === "Sin entrega")    return { bg:"#fff7ed", color:NARANJ,    border:"#ea580c", txt:"⚠ Sin entrega"   };
    if (est === "Reprogramado")   return { bg:"#faf5ff", color:"#7c3aed", border:"#a78bfa", txt:"🔁 Reprogramado" };
    return                               { bg:"#f8fafc", color:ROJO,      border:"#fca5a5", txt:"⏳ Pendiente"     };
  };

  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      try {
        const params = new URLSearchParams();
        if (filtroMes) params.append("mes", filtroMes);
        const res  = await fetch(`${process.env.REACT_APP_API_URL}/priorizacion-huella-jefe?${params}`);
        const json = await res.json();
        setDatos(json);
      } catch { setDatos([]); }
      finally { setCargando(false); }
    };
    cargar();
  }, [filtroMes]);

  const toggle = (setter, key) => setter(e => ({ ...e, [key]: !e[key] }));

  // En "Todos los meses" un mismo nodo puede aparecer varias veces — una
  // vez por cada ciclo de priorización (si se reprogramó). Para que los
  // totales no lo cuenten más de una vez, nos quedamos con un solo
  // registro por nodo: el de su ciclo más reciente (su estado final). El
  // detalle sigue mostrando el historial completo. Misma lógica que Tabla 1.
  const dedupePorNodoFinal = (nodos) => {
    const porNodo = {};
    nodos.forEach(n => {
      const actual = porNodo[n.id_nodo];
      if (!actual || (n.mes_priorizacion || "") > (actual.mes_priorizacion || "")) {
        porNodo[n.id_nodo] = n;
      }
    });
    return Object.values(porNodo);
  };

  const kpiEstados = {
    "Certificados":      ["Certificado"],
    "Cert. otro mes":    ["Cert. otro mes"],
    "Sin entrega":       ["Sin entrega"],
    "Pendientes":        ["Pendiente"],
    "Reprogramado":      ["Reprogramado"],
    "No entregados mes": ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"],
  };

  // Lista de jefes de zona disponibles para el filtro
  const jefesDisponibles = Array.from(new Set(datos.map(r => r.jefe_zona).filter(Boolean))).sort();

  // Mes "objetivo" del filtro actual — igual que en Tabla 1.
  const mesObjetivo = (filtroMes && filtroMes !== "todos") ? filtroMes : mesActual;

  const q = busqueda.toLowerCase().trim();

  // ── FILTRADO con recálculo de subtotales ───────────────────
  const filtrados = (filtroJefe ? datos.filter(r => r.jefe_zona === filtroJefe) : datos).map(r => {
    // 1. Filtro KPI sobre nodos (recorre aliado → ciudad → nodos)
    let aliadosBase = r.aliados || [];

    if (filtroKpi === "Certificados Backlog") {
      // Filtro especial: no es por estado, es por el flag real de backlog
      aliadosBase = aliadosBase.map(a => {
        const ciudFilt = (a.ciudades||[]).map(c => {
          const nFilt = (c.nodos||[]).filter(n => n.es_backlog_activo === 1);
          return nFilt.length ? { ...c, nodos: nFilt } : null;
        }).filter(Boolean);
        return ciudFilt.length ? { ...a, ciudades: ciudFilt } : null;
      }).filter(Boolean);
      if (!aliadosBase.length) return null;
    } else if (filtroKpi && kpiEstados[filtroKpi]) {
      const valid = kpiEstados[filtroKpi];
      aliadosBase = aliadosBase.map(a => {
        const ciudFilt = (a.ciudades||[]).map(c => {
          const nFilt = (c.nodos||[]).filter(n =>
            valid.includes(n.estado)
          );
          return nFilt.length ? { ...c, nodos: nFilt } : null;
        }).filter(Boolean);
        return ciudFilt.length ? { ...a, ciudades: ciudFilt } : null;
      }).filter(Boolean);
      if (!aliadosBase.length) return null;
    }

    if (q) {
      const jefeOk = r.jefe_zona?.toLowerCase().includes(q);

      if (jefeOk) {
        // El jefe coincide → mostrar todos sus aliados sin filtrar
        aliadosBase = r.aliados || [];
      } else {
        aliadosBase = aliadosBase.map(a => {
          const aliadoOk = a.aliado?.toLowerCase().includes(q);
          if (aliadoOk) return a; // aliado coincide → todas sus ciudades y nodos

          const ciudFilt = (a.ciudades||[]).map(c => {
            const ciudOk = c.ciudad?.toLowerCase().includes(q);
            if (ciudOk) return c; // ciudad coincide → todos sus nodos
            const nFilt = (c.nodos||[]).filter(n =>
              n.id_nodo?.toLowerCase().includes(q) || n.nombre_nodo?.toLowerCase().includes(q)
            );
            if (!nFilt.length) return null;
            return { ...c, nodos: nFilt };
          }).filter(Boolean);

          if (!ciudFilt.length) return null;
          return { ...a, ciudades: ciudFilt };
        }).filter(Boolean);

        if (!aliadosBase.length) return null;
      }
    }

    // 2. Recalcular subtotales de ciudad y aliado — todo sobre nodosDelMes
    // (su propia priorización = mes objetivo, o deduplicado por nodo si se
    // ve "Todos los meses"); el backlog, sobre TODOS los nodos que llegaron.
    // Misma lógica que Tabla 1.
    const alidosRecalc = aliadosBase.map(a => {
      const ciudadesBase = a.ciudades || [];
      const ciudadesRecalc = ciudadesBase.map(c => {
        const nodos = c.nodos || [];
        const nodosDelMes = filtroMes === "todos" ? dedupePorNodoFinal(nodos) : nodos.filter(n => n.mes_priorizacion === mesObjetivo);
        const cert  = nodosDelMes.filter(n => n.estado === "Certificado").length;
        const otro  = nodosDelMes.filter(n => n.estado === "Cert. otro mes").length;
        const pend  = nodosDelMes.filter(n => n.estado === "Pendiente").length;
        const se    = nodosDelMes.filter(n => n.estado === "Sin entrega").length;
        const repr  = nodosDelMes.filter(n => n.estado === "Reprogramado").length;
        const back  = nodos.filter(n => n.es_backlog_activo === 1).length;
        const falt  = nodosDelMes.filter(n => ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"].includes(n.estado)).length;
        const prio  = nodosDelMes.length;
        const pctAv = prio > 0 ? Math.round((cert + otro) / prio * 100) : 0;
        const hhppC = nodosDelMes.reduce((s, n) => s + (Number(n.hhpp) || 0), 0);
        return { ...c, priorizados: prio, certificados: cert, cert_otro_mes: otro, pendientes: pend, sin_entrega: se, reprogramados: repr, cert_backlog_activo: back, faltantes: falt, pct_avance: pctAv, hhpp: hhppC };
      });

      const nodosAliado = ciudadesBase.flatMap(c => c.nodos || []);
      const aNodosDelMes = filtroMes === "todos" ? dedupePorNodoFinal(nodosAliado) : nodosAliado.filter(n => n.mes_priorizacion === mesObjetivo);
      const aCert = aNodosDelMes.filter(n => n.estado === "Certificado").length;
      const aOtro = aNodosDelMes.filter(n => n.estado === "Cert. otro mes").length;
      const aPend = aNodosDelMes.filter(n => n.estado === "Pendiente").length;
      const aSe   = aNodosDelMes.filter(n => n.estado === "Sin entrega").length;
      const aRepr = aNodosDelMes.filter(n => n.estado === "Reprogramado").length;
      const aBack = nodosAliado.filter(n => n.es_backlog_activo === 1).length;
      const aFalt = aNodosDelMes.filter(n => ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"].includes(n.estado)).length;
      const aPrio = aNodosDelMes.length;
      const aPct  = aPrio > 0 ? Math.round((aCert + aOtro) / aPrio * 100) : 0;
      const aHhpp = aNodosDelMes.reduce((s, n) => s + (Number(n.hhpp) || 0), 0);
      return { ...a, ciudades: ciudadesRecalc, priorizados: aPrio, certificados: aCert, cert_otro_mes: aOtro, pendientes: aPend, sin_entrega: aSe, reprogramados: aRepr, cert_backlog_activo: aBack, faltantes: aFalt, pct_avance: aPct, hhpp: aHhpp };
    });

    // 3. Recalcular subtotales de jefe desde aliados BASE (crudos, antes de
    // restringir por mes) — mismo criterio.
    const nodosJefe = aliadosBase.flatMap(a => (a.ciudades||[]).flatMap(c => c.nodos||[]));
    const jNodosDelMes = filtroMes === "todos" ? dedupePorNodoFinal(nodosJefe) : nodosJefe.filter(n => n.mes_priorizacion === mesObjetivo);
    const jCert = jNodosDelMes.filter(n => n.estado === "Certificado").length;
    const jOtro = jNodosDelMes.filter(n => n.estado === "Cert. otro mes").length;
    const jPend = jNodosDelMes.filter(n => n.estado === "Pendiente").length;
    const jSe   = jNodosDelMes.filter(n => n.estado === "Sin entrega").length;
    const jRepr = jNodosDelMes.filter(n => n.estado === "Reprogramado").length;
    const jBack = nodosJefe.filter(n => n.es_backlog_activo === 1).length;
    const jFalt = jNodosDelMes.filter(n => ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"].includes(n.estado)).length;
    const jPrio = jNodosDelMes.length;
    const jPct  = jPrio > 0 ? Math.round((jCert + jOtro) / jPrio * 100) : 0;
    const jHhpp = jNodosDelMes.reduce((s, n) => s + (Number(n.hhpp) || 0), 0);

    return {
      ...r,
      aliados:             alidosRecalc,
      priorizados:         jPrio,
      certificados:        jCert,
      cert_otro_mes:       jOtro,
      pendientes:          jPend,
      sin_entrega:         jSe,
      reprogramados:       jRepr,
      cert_backlog_activo: jBack,
      faltantes:           jFalt,
      pct_avance:          jPct,
      hhpp:                jHhpp,
    };
  }).filter(Boolean);

  const esMesActualColJ = !filtroMes || filtroMes === mesActual;
  const todosMesesColJ  = filtroMes === "todos";

  // ── Totales globales desde nodos filtrados reales ──────────
  // Misma lógica que Tabla 1: todo sobre el mes objetivo (la meta oficial),
  // deduplicado por nodo cuando se ve "Todos los meses"; el backlog, sobre
  // TODOS los nodos que llegaron.
  const todosNodosFiltrados = filtrados.flatMap(r =>
    (r.aliados||[]).flatMap(a => (a.ciudades||[]).flatMap(c => c.nodos||[]))
  );
  const nodosDelMesGlobal = filtroMes === "todos" ? dedupePorNodoFinal(todosNodosFiltrados) : todosNodosFiltrados.filter(n => n.mes_priorizacion === mesObjetivo);
  const totGlobal = {
    prio: nodosDelMesGlobal.length,
    cert: nodosDelMesGlobal.filter(n => n.estado === "Certificado").length,
    back: nodosDelMesGlobal.filter(n => n.estado === "Cert. otro mes").length,
    otro: nodosDelMesGlobal.filter(n => n.estado === "Cert. otro mes").length,
    pend: nodosDelMesGlobal.filter(n => n.estado === "Pendiente").length,
    se:   nodosDelMesGlobal.filter(n => n.estado === "Sin entrega").length,
    repr: nodosDelMesGlobal.filter(n => n.estado === "Reprogramado").length,
    falt: nodosDelMesGlobal.filter(n => ["Pendiente","Sin entrega","Cert. otro mes","Reprogramado"].includes(n.estado)).length,
    hhpp: nodosDelMesGlobal.reduce((s, n) => s + (Number(n.hhpp) || 0), 0),
    // Certificados Backlog: sobre TODOS los nodos filtrados, no solo los del mes objetivo
    backlogReal: todosNodosFiltrados.filter(n => n.es_backlog_activo === 1).length,
  };
  totGlobal.pct = totGlobal.prio > 0
    ? Math.round((totGlobal.cert + totGlobal.otro) / totGlobal.prio * 100)
    : 0;

  // Nota: Tabla 2 no maneja una "meta" propia por jefe de zona (no existe
  // ese dato en el sistema — la meta solo se define por aliado). Por eso
  // aquí el % de avance se calcula contra lo PRIORIZADO (certificados /
  // priorizados), no contra una meta externa — a diferencia de Tabla 1.

  // Auto-expandir al buscar
  useEffect(() => {
    if (!q) return;
    const nA = {}, nAl = {}, nC = {};
    filtrados.forEach(r => {
      nA[r.jefe_zona] = true;
      r.aliados?.forEach(a => {
        nAl[`${r.jefe_zona}__${a.aliado}`] = true;
        a.ciudades?.forEach(c => {
          nC[`${r.jefe_zona}__${a.aliado}__${c.ciudad}`] = true;
        });
      });
    });
    setExpJefe(nA); setExpAliado(nAl); setExpCiudad(nC);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda]);

  const expandAll = () => {
    const nA = {}, nAl = {}, nC = {};
    filtrados.forEach(r => {
      nA[r.jefe_zona] = true;
      r.aliados?.forEach(a => {
        nAl[`${r.jefe_zona}__${a.aliado}`] = true;
        a.ciudades?.forEach(c => { nC[`${r.jefe_zona}__${a.aliado}__${c.ciudad}`] = true; });
      });
    });
    setExpJefe(nA); setExpAliado(nAl); setExpCiudad(nC);
  };
  const collapseAll = () => { setExpJefe({}); setExpAliado({}); setExpCiudad({}); };

  const descargarCSVT2 = () => {
    const enc = (v) => `"${String(v ?? "").replace(/"/g,'""')}"`;
    const cols = ["Jefe Zona","Aliado","Ciudad","Nodo","Mes Priorizacion","Cal. ICR","HHPP","Estado","Cert. Meta Mes","Cert. Backlog","OT / CHG","Fecha Cert."];
    const filas = [cols.join(";")];
    filtrados.forEach(jefe => {
      (jefe.aliados || []).forEach(aliado => {
        (aliado.ciudades || []).forEach(ciudad => {
          (ciudad.nodos || []).forEach(nodo => {
            filas.push([
              enc(jefe.jefe_zona), enc(aliado.aliado), enc(ciudad.ciudad), enc(nodo.id_nodo),
              enc(nodo.mes_priorizacion || ""),
              enc(nodo.priorizacion_calidad ?? ""), enc(nodo.hhpp ?? ""),
              enc(nodo.estado || ""),
              nodo.estado === "Certificado" ? 1 : 0,
              nodo.es_backlog_activo ? 1 : 0,
              enc(nodo.ot_chg || ""), enc(nodo.fecha_cert || "")
            ].join(";"));
          });
        });
      });
    });
    const blob = new Blob(["\uFEFF" + filas.join("\n")], { type:"text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `T2_PriorizacionHuella_${filtroMes || "mes_actual"}.csv`;
    a.click();
  };

  const thSt = { padding:"8px 10px", textAlign:"left", fontWeight:"700", color:CLARO_MUTED, fontSize:"10px", textTransform:"uppercase", letterSpacing:"0.04em", borderBottom:"1px solid #e2e8f0", whiteSpace:"normal", lineHeight:"1.4", verticalAlign:"bottom", background:CLARO_BG };

  const numCell = (val, color) => (
    <td style={{ padding:"9px 10px", fontWeight:"700", color: val > 0 ? color : CLARO_MUTED }}>{val}</td>
  );

  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px", marginBottom:"20px" }}>
      <div style={{ marginBottom:"14px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"3px" }}>
          <div style={{ width:"4px", height:"18px", background:CLARO_RED, borderRadius:"2px" }} />
          <div style={{ fontSize:"14px", fontWeight:"700", color:CLARO_DARK }}>
            Tabla 2 — Priorizados vs Certificados por Jefe de Zona
          </div>
        </div>
        <div style={{ fontSize:"11px", color:CLARO_MUTED, marginLeft:"12px" }}>
          Haz clic en ▶ para ver el detalle por aliado, ciudad y nodo. Las tarjetas de arriba filtran la tabla — clic para activar.
        </div>
      </div>

      {/* KPIs — ahora muestran totales de los nodos filtrados */}
      <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:"14px", alignItems:"center" }}>
        {(() => {
          const esMesActualT2 = !filtroMes || filtroMes === mesActual;
          const todosMesesT2  = filtroMes === "todos";
          return [
            { label:"🎯 Priorizados",         sub: (totGlobal.prio > 0 ? `${Math.round(totGlobal.cert / totGlobal.prio * 100)}% avance — ` : "") + "Total asignados", valor:totGlobal.prio, color:CLARO_DARK, kpi:null,             show:true },
            { label:"✅ Certificados",        sub:"Priorizados y certificados en el mismo mes", valor:totGlobal.cert, color:VERDE,      kpi:"Certificados",    show:true },
            { label:"↗ Cert. otro mes",      sub:"Priorizados este mes, pero certificados en un mes diferente", valor:totGlobal.otro, color:"#2563eb", kpi:"Cert. otro mes", show:true },
            { label:"📦 Certificados Backlog", sub:"Certificados este mes, pero priorizados en un mes anterior (no son de la meta oficial)", valor:totGlobal.backlogReal, color:"#0891b2",  kpi:"Certificados Backlog",   show:true },
            { label:"⏳ Pendientes",          sub:"Sin cert. en mes actual",                valor:totGlobal.pend, color:"#7c3aed",  kpi:"Pendientes",      show:todosMesesT2 || esMesActualT2 },
            { label:"🔴 Bolsa Nodos",        sub:"Nodos Priorizados pero no Ejecutados y/o Entregados", valor:totGlobal.se, color:NARANJ, kpi:"Sin entrega", show:todosMesesT2 || !esMesActualT2 },
            { label:"🔁 Reprogramados",      sub:"Nodos de este mes reprogramados a un mes posterior", valor:totGlobal.repr, color:"#9333ea", kpi:"Reprogramado", show:true },
          ].filter(t => t.show);
        })().map(({ label, valor, color, kpi, sub }) => {
          const activo = filtroKpi === kpi && kpi !== null;
          return (
            <div key={label}
              onClick={() => { setFiltroKpi(activo ? null : kpi); setExpJefe({}); setExpAliado({}); setExpCiudad({}); }}
              style={{
                background: activo ? color : "white",
                borderRadius:"10px", padding:"10px 16px", textAlign:"center", minWidth:"110px",
                cursor: kpi ? "pointer" : "default",
                border: activo ? `2px solid ${color}` : `1px solid #e2e8f0`,
                borderTop: activo ? `2px solid ${color}` : `3px solid ${color}`,
                transition:"all 0.15s",
                boxShadow: activo ? `0 2px 10px ${color}40` : "0 1px 3px rgba(0,0,0,0.06)",
              }}
              title={kpi ? `Clic para filtrar por: ${label}` : undefined}
            >
              <div style={{ fontSize:"12px", fontWeight:"700", color: activo ? "white" : CLARO_DARK, marginBottom:"4px" }}>{label}</div>
              <div style={{ fontSize:"26px", fontWeight:"800", color: activo ? "white" : color, lineHeight:1 }}>{fmt(valor)}</div>
              {sub && <div style={{ fontSize:"9px", color: activo ? "rgba(255,255,255,0.85)" : CLARO_MUTED, marginTop:"4px", lineHeight:"1.3" }}>{sub}</div>}
              {kpi && <div style={{ fontSize:"8px", color: activo ? "rgba(255,255,255,0.7)" : CLARO_LIGHT, marginTop:"3px" }}>{activo ? "✕ limpiar filtro" : "↑ clic para filtrar"}</div>}
            </div>
          );
        })}
      </div>

      <div style={{ fontSize:"10.5px", color:CLARO_MUTED, marginBottom:"12px", lineHeight:"1.4" }}>
        🎯 Priorizados = ✅ Certificados + ↗ Cert. otro mes + 🔴 Bolsa Nodos + 🔁 Reprogramados (+ ⏳ Pendientes cuando aplica).
        📦 Certificados Backlog es aparte: cuenta nodos de <strong>otros</strong> meses certificados ahora, no es parte de los Priorizados de este mes.
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"12px", alignItems:"center" }}>
        {filtroKpi && (
          <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 10px", borderRadius:"8px", background:"#f3f0ff", border:"1px solid #c4b5fd", fontSize:"11px", color:"#7c3aed", fontWeight:"600" }}>
            Filtrando: {filtroKpi}
            <span onClick={()=>setFiltroKpi(null)} style={{ cursor:"pointer", fontWeight:"700", fontSize:"13px" }}>✕</span>
          </div>
        )}
        <input type="text" placeholder="🔍 Buscar jefe, aliado, ciudad o nodo..." value={busqueda}
          onChange={e=>setBusqueda(e.target.value)}
          style={{ padding:"7px 11px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, outline:"none", minWidth:"240px" }}
          onFocus={e=>e.target.style.borderColor=CLARO_RED}
          onBlur={e=>e.target.style.borderColor="#e2e8f0"} />
        <select value={filtroJefe} onChange={e=>{ setFiltroJefe(e.target.value); setExpJefe({}); setExpAliado({}); setExpCiudad({}); }}
          style={{ padding:"7px 11px", border: filtroJefe ? `1.5px solid ${CLARO_RED}` : "1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Todos los jefes de zona</option>
          {jefesDisponibles.map(j => <option key={j} value={j}>{j}</option>)}
        </select>
        <select value={filtroMes} onChange={e=>setFiltroMes(e.target.value)}
          style={{ padding:"7px 11px", border: filtroMes === "todos" ? `1.5px solid #0891b2` : "1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Mes actual</option>
          <option value="todos">Todos los meses</option>
          {Object.entries(mesesLabel).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {/* Botón limpiar filtros — visible solo si hay algo activo */}
        {(busqueda || filtroMes || filtroJefe || filtroKpi) && (
          <button
            onClick={() => { setBusqueda(""); setFiltroMes(""); setFiltroJefe(""); setFiltroKpi(null); setExpJefe({}); setExpAliado({}); setExpCiudad({}); }}
            style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:"#f8fafc", cursor:"pointer", color:CLARO_RED, display:"flex", alignItems:"center", gap:"5px" }}>
            ✕ Limpiar filtros
          </button>
        )}
        <button onClick={expandAll} style={{ padding:"7px 12px", borderRadius:"8px", fontSize:"11px", fontWeight:"600", border:"1px solid #e2e8f0", background:"white", cursor:"pointer", color:CLARO_MUTED }}>↕ Expandir todo</button>
        <button onClick={collapseAll} style={{ padding:"7px 12px", borderRadius:"8px", fontSize:"11px", fontWeight:"600", border:"1px solid #e2e8f0", background:"white", cursor:"pointer", color:CLARO_MUTED }}>↕ Colapsar todo</button>
        <button onClick={descargarCSVT2}
          style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:CLARO_RED, cursor:"pointer", color:"white", display:"flex", alignItems:"center", gap:"5px", marginLeft:"auto" }}>
          ⬇ Descargar CSV
        </button>
      </div>

      {filtroMes === "todos" && (
        <div style={{ background:"#ecfeff", border:"1px solid #a5f3fc", borderRadius:"8px", padding:"10px 14px", marginBottom:"14px", fontSize:"11.5px", color:"#0e7490", lineHeight:"1.5" }}>
          ℹ️ <strong>Viendo todos los meses:</strong> si un nodo fue reprogramado de un mes a otro, los totales (Priorizados, Certificados, Pendientes, etc.) lo cuentan <strong>una sola vez</strong>, con su estado más reciente — no se duplica entre el mes viejo y el nuevo.
          El detalle debajo de cada ciudad sí muestra el historial completo (todas sus filas, incluida la que dice "Reprogramado"), para que puedas ver por qué cambió de mes.
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign:"center", padding:"32px", color:CLARO_MUTED, fontSize:"13px" }}>Cargando...</div>
      ) : (
        <div style={{ overflowX:"auto", borderRadius:"8px", border:"1px solid #e2e8f0" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px", tableLayout:"fixed" }}>
            <thead>
              <tr>
                <th style={{...thSt, width:"16%"}}>Jefe / Aliado / Ciudad / Nodo</th>
                <th style={{...thSt, width:"6%"}}>Mes prio.</th>
                <th style={{...thSt, width:"5%"}} title="Priorización de calidad ICR">Cal. ICR</th>
                <th style={{...thSt, width:"5%"}} title="Horas hombre programadas">HHPP</th>
                <th style={{...thSt, width:"5%"}} title="Total nodos asignados en el mes">Asignados</th>
                <th style={{...thSt, width:"6%", color:VERDE}} title="Priorizados y certificados en el mismo mes — la meta oficial del mes">✅ Cert.<br/>meta mes</th>
                <th style={{...thSt, width:"7%", color:"#0891b2"}} title="Certificados este mes, pero priorizados en un mes anterior — no son de la meta oficial del mes">📦 Cert.<br/>Backlog</th>
                <th style={{...thSt, width: todosMesesColJ ? "12%" : "6%", color: esMesActualColJ ? "#7c3aed" : NARANJ}} title={esMesActualColJ ? "Sin certificar en el mes actual" : "Nodos de meses anteriores sin certificar"}>
                  {todosMesesColJ ? "⏳ Pend. / 🔴 Bolsa" : esMesActualColJ ? "⏳ Pendientes" : "🔴 Bolsa Nodos"}
                </th>
                <th style={{...thSt, width:"12%"}}>% Avance</th>
                <th style={{...thSt, width:"8%"}}>OT / CHG</th>
                <th style={{...thSt, width:"8%"}}>Fecha cert.</th>
                <th style={{...thSt, width:"9%"}}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 ? (
                <tr><td colSpan="13" style={{ padding:"28px", textAlign:"center", color:"#94a3b8" }}>Sin resultados</td></tr>
              ) : filtrados.map(jefeRow => (
                <>
                  {/* ── JEFE ZONA ── */}
                  <tr key={jefeRow.jefe_zona}
                    onClick={() => toggle(setExpJefe, jefeRow.jefe_zona)}
                    style={{ background:"white", cursor:"pointer", borderLeft:"4px solid #E30613" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"}
                    onMouseLeave={e=>e.currentTarget.style.background="white"}>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_DARK }}>
                      <span style={{ marginRight:"8px", color:CLARO_RED }}>{expJefe[jefeRow.jefe_zona]?"▼":"▶"}</span>
                      {jefeRow.jefe_zona}
                      <span style={{ marginLeft:"8px", fontSize:"10px", color:"#9ca3af", fontWeight:"400" }}>
                        ({jefeRow.aliados?.length} aliado{jefeRow.aliados?.length!==1?"s":""})
                      </span>
                    </td>
                    <td></td>{/* Mes prio. vacío */}
                    <td></td>{/* Cal.ICR vacío */}
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_MUTED, textAlign:"right", fontSize:"12px" }}>{jefeRow.hhpp ? fmt(jefeRow.hhpp) : "—"}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_DARK }}>{jefeRow.priorizados}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:VERDE }}>{jefeRow.certificados}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color:"#0891b2" }}>{jefeRow.cert_backlog_activo||0}</td>
                    <td style={{ padding:"10px 14px", fontWeight:"700", color: esMesActualColJ ? "#7c3aed" : NARANJ }}>
                      {todosMesesColJ ? `${jefeRow.pendientes||0} / ${jefeRow.sin_entrega||0}` : esMesActualColJ ? (jefeRow.pendientes||0) : (jefeRow.sin_entrega||0)}
                    </td>
                    <td style={{ padding:"10px 14px" }}>
                      <BarraAvance
                        pct={jefeRow.pct_avance}
                        pctMeta={jefeRow.priorizados > 0 ? Math.round(
                          (esMesActualColJ
                            ? jefeRow.certificados + (jefeRow.cert_otro_mes||0)
                            : todosMesesColJ
                            ? jefeRow.priorizados - (jefeRow.sin_entrega||0) - (jefeRow.pendientes||0)
                            : jefeRow.priorizados - (jefeRow.sin_entrega||0)
                          ) / jefeRow.priorizados * 100) : 0}
                        metaLabel="% Avance"
                        esMesActual={!filtroMes || filtroMes === mesActual}
                        bolsaCerrada={!!filtroMes && filtroMes !== mesActual}
                        sinEntrega={jefeRow.sin_entrega}
                        pctConBacklog={jefeRow.priorizados > 0 ? Math.round(((esMesActualColJ ? jefeRow.certificados + (jefeRow.cert_otro_mes||0) : jefeRow.priorizados - (jefeRow.sin_entrega||0)) + (jefeRow.cert_backlog_activo||0)) / jefeRow.priorizados * 100) : null}
                        backlogLabel="% Con backlog"
                      />
                    </td>
                    <td colSpan="3"></td>
                  </tr>

                  {/* ── ALIADO ── */}
                  {expJefe[jefeRow.jefe_zona] && jefeRow.aliados?.map(aRow => {
                    const akJefe = `${jefeRow.jefe_zona}__${aRow.aliado}`;
                    return (
                      <>
                        <tr key={akJefe}
                          onClick={() => toggle(setExpAliado, akJefe)}
                          style={{ background:"#f3f4f6", cursor:"pointer", borderBottom:"1px solid #e2e8f0" }}
                          onMouseEnter={e=>e.currentTarget.style.background="#e5e7eb"}
                          onMouseLeave={e=>e.currentTarget.style.background="#f3f4f6"}>
                          <td style={{ padding:"9px 14px 9px 28px", fontWeight:"600", color:"#1f2937" }}>
                            <span style={{ marginRight:"8px", fontSize:"10px", color:CLARO_RED }}>{expAliado[akJefe]?"▼":"▶"}</span>
                            {aRow.aliado}
                            <span style={{ marginLeft:"8px", fontSize:"10px", color:"#9ca3af", fontWeight:"400" }}>
                              ({aRow.ciudades?.length} ciudad{aRow.ciudades?.length!==1?"es":""})
                            </span>
                          </td>
                          <td></td>{/* Mes prio. vacío */}
                          <td></td>{/* Cal.ICR vacío */}
                          <td style={{ padding:"9px 10px", fontWeight:"600", color:CLARO_MUTED, textAlign:"right", fontSize:"11px" }}>{aRow.hhpp ? fmt(aRow.hhpp) : "—"}</td>
                          <td style={{ padding:"9px 10px", fontWeight:"600", color:"#1f2937" }}>{aRow.priorizados}</td>
                          <td style={{ padding:"9px 10px", fontWeight:"600", color:VERDE }}>{aRow.certificados}</td>
                          <td style={{ padding:"9px 10px", fontWeight:"600", color:aRow.cert_backlog_activo>0?"#0891b2":CLARO_MUTED }}>{aRow.cert_backlog_activo||0}</td>
                          <td style={{ padding:"9px 10px", fontWeight:"600", color: esMesActualColJ ? "#7c3aed" : NARANJ }}>
                            {todosMesesColJ ? `${aRow.pendientes||0} / ${aRow.sin_entrega||0}` : esMesActualColJ ? (aRow.pendientes||0) : (aRow.sin_entrega||0)}
                          </td>
                          <td style={{ padding:"9px 10px" }}>
                            <BarraAvance
                              pct={aRow.pct_avance}
                              pctMeta={aRow.priorizados > 0 ? Math.round(
                                (esMesActualColJ
                                  ? aRow.certificados + (aRow.cert_otro_mes||0)
                                  : todosMesesColJ
                                  ? aRow.priorizados - (aRow.sin_entrega||0) - (aRow.pendientes||0)
                                  : aRow.priorizados - (aRow.sin_entrega||0)
                                ) / aRow.priorizados * 100) : 0}
                              metaLabel="% Cumpl."
                              esMesActual={!filtroMes || filtroMes === mesActual}
                              bolsaCerrada={!!filtroMes && filtroMes !== mesActual}
                              sinEntrega={aRow.sin_entrega}
                              pctConBacklog={aRow.priorizados > 0 ? Math.round(((esMesActualColJ ? aRow.certificados + (aRow.cert_otro_mes||0) : aRow.priorizados - (aRow.sin_entrega||0)) + (aRow.cert_backlog_activo||0)) / aRow.priorizados * 100) : null}
                              backlogLabel="% Con backlog"
                            />
                          </td>
                          <td colSpan="3"></td>
                        </tr>

                        {/* ── CIUDAD ── */}
                        {expAliado[akJefe] && aRow.ciudades?.map(cRow => {
                          const ckAll = `${jefeRow.jefe_zona}__${aRow.aliado}__${cRow.ciudad}`;
                          return (
                            <>
                              <tr key={ckAll}
                                onClick={() => toggle(setExpCiudad, ckAll)}
                                style={{ background:"#f9fafb", cursor:"pointer", borderBottom:"1px solid #e5e7eb" }}
                                onMouseEnter={e=>e.currentTarget.style.background="#e2e8f0"}
                                onMouseLeave={e=>e.currentTarget.style.background="#f8fafc"}>
                                <td style={{ padding:"8px 14px 8px 42px", fontWeight:"600", color:CLARO_DARK }}>
                                  <span style={{ marginRight:"8px", fontSize:"10px", color:"#6366f1" }}>{expCiudad[ckAll]?"▼":"▶"}</span>
                                  📍 {cRow.ciudad}
                                  <span style={{ marginLeft:"8px", fontSize:"10px", color:CLARO_MUTED, fontWeight:"400" }}>
                                    ({cRow.nodos?.length} nodo{cRow.nodos?.length!==1?"s":""})
                                  </span>
                                </td>
                                <td></td>{/* Mes prio. vacío */}
                                <td></td>{/* Cal.ICR vacío */}
                                <td style={{ padding:"9px 10px", fontWeight:"600", color:CLARO_MUTED, textAlign:"right", fontSize:"11px" }}>{cRow.hhpp ? fmt(cRow.hhpp) : "—"}</td>
                                {numCell(cRow.priorizados, CLARO_DARK)}
                                {numCell(cRow.certificados, VERDE)}
                                <td style={{ padding:"9px 10px", fontWeight:"600", color:cRow.cert_backlog_activo>0?"#0891b2":CLARO_MUTED }}>{cRow.cert_backlog_activo||0}</td>
                                <td style={{ padding:"9px 10px", fontWeight:"600", color: esMesActualColJ ? (cRow.pendientes>0?"#7c3aed":CLARO_MUTED) : (cRow.sin_entrega>0?NARANJ:CLARO_MUTED) }}>
                                  {todosMesesColJ ? `${cRow.pendientes||0} / ${cRow.sin_entrega||0}` : esMesActualColJ ? (cRow.pendientes||0) : (cRow.sin_entrega||0)}
                                </td>
                                <td style={{ padding:"8px 10px" }}>
                                  <BarraAvance
                                    pct={cRow.priorizados > 0 ? Math.round(cRow.certificados / cRow.priorizados * 100) : 0}
                                    pctMeta={cRow.priorizados > 0 ? Math.round(
                                      (esMesActualColJ
                                        ? cRow.certificados + (cRow.cert_otro_mes||0)
                                        : todosMesesColJ
                                        ? cRow.priorizados - (cRow.sin_entrega||0) - (cRow.pendientes||0)
                                        : cRow.priorizados - (cRow.sin_entrega||0)
                                      ) / cRow.priorizados * 100) : 0}
                                    metaLabel="% Cumpl."
                                    esMesActual={!filtroMes || filtroMes === mesActual}
                                    bolsaCerrada={!!filtroMes && filtroMes !== mesActual}
                                    sinEntrega={cRow.sin_entrega}
                                    soloPendientes={cRow.certificados === 0 && cRow.cert_otro_mes === 0 && cRow.sin_entrega === 0 && cRow.pendientes > 0}
                                    pctConBacklog={cRow.priorizados > 0 ? Math.round(((esMesActualColJ ? cRow.certificados + (cRow.cert_otro_mes||0) : cRow.priorizados - (cRow.sin_entrega||0)) + (cRow.cert_backlog_activo||0)) / cRow.priorizados * 100) : null}
                                    backlogLabel="% Con backlog"
                                  />
                                </td>
                                <td colSpan="3"></td>
                              </tr>

                              {/* ── NODO ── */}
                              {expCiudad[ckAll] && cRow.nodos?.map((nodo, ni) => {
                                const es = estadoStyle(nodo.estado);
                                return (
                                  <tr key={`${ckAll}-${ni}`}
                                    style={{ borderBottom:"1px solid #f1f5f9" }}
                                    onMouseEnter={e=>e.currentTarget.style.background="#fafafa"}
                                    onMouseLeave={e=>e.currentTarget.style.background="white"}>
                                    <td style={{ padding:"7px 14px 7px 58px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                                      <span style={{ color:"#cbd5e1", marginRight:"6px", fontSize:"11px" }}>└</span>
                                      <span style={{ fontWeight:"700", color:CLARO_DARK, marginRight:"6px" }}>{nodo.id_nodo}</span>
                                      <span style={{ color:CLARO_MUTED, fontSize:"11px" }}>{nodo.nombre_nodo !== nodo.id_nodo ? nodo.nombre_nodo : ""}</span>
                                      {nodo.estado === "Reprogramado" && nodo.veces_reprogramado > 0 && (
                                        <span title={`Reprogramado ${nodo.veces_reprogramado} vez(ces)`} style={{ marginLeft:"6px", display:"inline-flex", alignItems:"center", gap:"3px", background:"#faf5ff", color:"#7c3aed", border:"1px solid #a78bfa", borderRadius:"20px", fontSize:"10px", fontWeight:"700", padding:"1px 7px" }}>
                                          🔁 ×{nodo.veces_reprogramado}
                                        </span>
                                      )}
                                      {nodo.estado !== "Reprogramado" && nodo.veces_reprogramado > 0 && (
                                        <span title={`Fue reprogramado ${nodo.veces_reprogramado} vez(ces) antes de ejecutarse`} style={{ marginLeft:"6px", display:"inline-flex", alignItems:"center", gap:"3px", background:"#f5f3ff", color:"#9333ea", border:"1px solid #d8b4fe", borderRadius:"20px", fontSize:"10px", fontWeight:"600", padding:"1px 7px" }}>
                                          🔁 Reprogram. ×{nodo.veces_reprogramado}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ padding:"7px 10px", color:CLARO_MUTED, fontSize:"11px", whiteSpace:"nowrap" }}>{mesMostrar(nodo.mes_priorizacion)}</td>
                                    <td style={{ padding:"7px 10px", fontSize:"11px", fontWeight:"600", color:CLARO_DARK, textAlign:"right" }}>
                                      {nodo.priorizacion_calidad != null ? fmt(nodo.priorizacion_calidad) : "—"}
                                    </td>
                                    <td style={{ padding:"7px 10px", fontSize:"11px", color:CLARO_MUTED, textAlign:"right" }}>
                                      {nodo.hhpp != null ? fmt(nodo.hhpp) : "—"}
                                    </td>
                                    <td style={{ padding:"7px 10px", fontSize:"11px", fontWeight:"600", color:CLARO_DARK }}>1</td>
                                    <td style={{ padding:"7px 10px", fontSize:"11px", fontWeight:"600", color: nodo.estado==="Certificado"?VERDE:CLARO_MUTED }}>{nodo.estado==="Certificado"?"1":"0"}</td>
                                    <td style={{ padding:"7px 10px", fontSize:"11px", fontWeight:"600", color: nodo.es_backlog_activo===1?"#0891b2":CLARO_MUTED }}>{nodo.es_backlog_activo===1?"1":"0"}</td>
                                    <td style={{ padding:"7px 10px", fontSize:"11px", fontWeight:"600", color: esMesActualColJ?(nodo.estado==="Pendiente"?"#7c3aed":CLARO_MUTED):(nodo.estado==="Sin entrega"?NARANJ:CLARO_MUTED) }}>
                                      {todosMesesColJ?`${nodo.estado==="Pendiente"?"1":"0"}/${nodo.estado==="Sin entrega"?"1":"0"}`:esMesActualColJ?(nodo.estado==="Pendiente"?"1":"0"):(nodo.estado==="Sin entrega"?"1":"0")}
                                    </td>
                                    <td style={{ padding:"7px 10px" }}>
                                      <BarraAvance
                                        pct={["Certificado","Cert. otro mes","Reprogramado"].includes(nodo.estado) ? 100 : 0}
                                        etiqueta={
                                          nodo.estado === "Certificado"    ? "% Cumpl." :
                                          nodo.estado === "Cert. otro mes" ? "% Cumpl." :
                                          nodo.estado === "Reprogramado"   ? "% Cumpl." :
                                          nodo.estado === "Pendiente"      ? "% Cumpl.":
                                          "% Cumpl."
                                        }
                                      />
                                    </td>
                                    <td style={{ padding:"7px 10px", fontFamily:"monospace", fontSize:"11px", color:CLARO_MUTED }}>{nodo.ot_chg||"—"}</td>
                                    <td style={{ padding:"7px 10px", fontSize:"11px", whiteSpace:"nowrap" }}>
                                      {nodo.fecha_cert
                                        ? <span style={{ color: nodo.estado==="Cert. otro mes"?"#2563eb": nodo.estado==="Reprogramado"?"#7c3aed":CLARO_MUTED, fontWeight: ["Cert. otro mes","Reprogramado"].includes(nodo.estado)?"700":"400" }}>
                                            {mesMostrar(nodo.fecha_cert)}
                                          </span>
                                        : "—"}
                                    </td>
                                    <td style={{ padding:"7px 10px" }}>
                                      <span style={{ display:"inline-block", fontSize:"10px", fontWeight:"700", padding:"2px 8px", borderRadius:"20px", background:es.bg, color:es.color, border:`1px solid ${es.border}` }}>{es.txt}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </>
                          );
                        })}
                      </>
                    );
                  })}
                </>
              ))}

              {/* Total general — siempre desde nodos filtrados */}
              <tr style={{ background:"#f3f4f6", borderTop:"2px solid #e5e7eb", borderLeft:"4px solid "+CLARO_RED }}>
                <td style={{ padding:"10px 14px", fontWeight:"700", fontSize:"12px", color:CLARO_DARK }}>TOTAL GENERAL</td>
                <td></td>{/* Mes prio. vacío */}
                <td></td>{/* Cal. ICR vacío */}
                <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_MUTED, textAlign:"right", fontSize:"12px" }}>{totGlobal.hhpp ? fmt(totGlobal.hhpp) : "—"}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_DARK }}>{totGlobal.prio}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color:VERDE }}>{totGlobal.cert}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color:"#0891b2" }}>{totGlobal.backlogReal}</td>
                <td style={{ padding:"10px 14px", fontWeight:"700", color: esMesActualColJ ? "#7c3aed" : NARANJ }}>
                  {todosMesesColJ ? `${totGlobal.pend} / ${totGlobal.se}` : esMesActualColJ ? totGlobal.pend : totGlobal.se}
                </td>
                <td style={{ padding:"10px 14px" }}>
                  <BarraAvance
                    pct={totGlobal.pct}
                    pctMeta={totGlobal.prio > 0 ? Math.round(
                      esMesActualColJ
                        ? (totGlobal.cert + totGlobal.otro) / totGlobal.prio * 100
                        : todosMesesColJ
                        ? (totGlobal.prio - totGlobal.se - totGlobal.pend) / totGlobal.prio * 100
                        : (totGlobal.prio - totGlobal.se) / totGlobal.prio * 100
                    ) : 0}
                    metaLabel="% Avance"
                    esMesActual={!filtroMes || filtroMes === mesActual}
                    bolsaCerrada={!!filtroMes && filtroMes !== mesActual && filtroMes !== "todos"}
                    sinEntrega={totGlobal.se}
                    pctConBacklog={totGlobal.prio > 0 ? Math.round(((esMesActualColJ ? totGlobal.cert + totGlobal.otro : totGlobal.prio - totGlobal.se) + totGlobal.backlogReal) / totGlobal.prio * 100) : null}
                    backlogLabel="% Con backlog"
                  />
                </td>
                <td colSpan="3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TablaDetalle({ totalPriorizados, totalCertMismoMes, totalCertOtroMes, totalPendientes, totalSinEntrega, certBacklogMes, totalCertificados, MESES_LABEL }) {
  const [filas, setFilas]           = useState([]);
  const [cargando, setCargando]     = useState(false);
  const [busqueda, setBusqueda]     = useState("");
  const [filtroMes, setFiltroMes]   = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroKpi, setFiltroKpi]   = useState(null);
  const [pagina, setPagina]         = useState(1);
  const POR_PAG = 15;

  // Mapa de tarjeta KPI → estado real en la BD (debe coincidir con los estados del detalle)
  const kpiEstadoMap = {
    "Cert. mismo mes": "Certificado",
    "Cert. Backlog":   "Cert. otro mes",   // backlog = certificado en mes distinto a su prio
    "Pendientes":      "Pendiente",
    "Bolsa Nodos":     "Sin entrega",
    "Cert. sin prio":  "Cert. sin prio",   // certificado sin ninguna programación
  };

  const estadoEfectivo = filtroKpi ? (kpiEstadoMap[filtroKpi] || "") : filtroEstado;

  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      try {
        const params = new URLSearchParams();
        if (busqueda)         params.append("busqueda", busqueda);
        if (filtroMes) params.append("mes", filtroMes);
        if (estadoEfectivo)   params.append("estado",   estadoEfectivo);
        const res  = await fetch(`${process.env.REACT_APP_API_URL}/priorizacion-huella-detalle?${params}`);
        const json = await res.json();
        setFilas(json);
        setPagina(1);
      } catch { setFilas([]); }
      finally { setCargando(false); }
    };
    const timer = setTimeout(cargar, 300);
    return () => clearTimeout(timer);
  }, [busqueda, filtroMes, estadoEfectivo]);

  const totalPags  = Math.max(1, Math.ceil(filas.length / POR_PAG));
  const paginaReal = Math.min(pagina, totalPags);
  const slice      = filas.slice((paginaReal-1)*POR_PAG, paginaReal*POR_PAG);

  const badgeEstado = (estado) => {
    const map = {
      "Certificado":    { bg:"#f0fdf4", color:"#16a34a", txt:"✓ Certificado" },
      "Cert. otro mes": { bg:"#eff6ff", color:"#2563eb", txt:"↗ Cert. otro mes" },
      "Cert. sin prio": { bg:"#f0f9ff", color:"#0891b2", txt:"📦 Cert. sin prio" },
      "Pendiente":      { bg:"#faf5ff", color:"#7c3aed", txt:"⏳ Pendiente" },
      "Sin entrega":    { bg:"#fff7ed", color:"#f97316", txt:"🔴 Sin entrega" },
      "Reprogramado":   { bg:"#faf5ff", color:"#7c3aed", txt:"🔁 Reprogramado" },
    };
    const s = map[estado] || { bg:"#f9fafb", color:"#6b7280", txt:estado };
    return <span style={{ display:"inline-block", fontSize:"11px", fontWeight:"700", padding:"3px 10px", borderRadius:"20px", background:s.bg, color:s.color }}>{s.txt}</span>;
  };

  // Conteos DINÁMICOS: se recalculan desde las filas filtradas para reflejar los filtros activos
  const kpiTotal     = filas.filter(n => n.estado !== "Cert. sin prio").length;
  const kpiCert      = filas.filter(n => n.estado === "Certificado").length;
  const kpiBacklog   = filas.filter(n => n.estado === "Cert. otro mes").length;
  const kpiSinPrio   = filas.filter(n => n.estado === "Cert. sin prio").length;
  const kpiPend      = filas.filter(n => n.estado === "Pendiente").length;
  const kpiSinEnt    = filas.filter(n => n.estado === "Sin entrega").length;
  const kpiTotalCert = kpiCert + kpiBacklog + kpiSinPrio;

  const kpis = [
    { label:"Total priorizados",    valor:kpiTotal,     color:CLARO_DARK,  kpi:null,            desc:"Nodos con priorización asignada" },
    { label:"✅ Cert. mismo mes",   valor:kpiCert,      color:VERDE,        kpi:"Cert. mismo mes", desc:"Certificados en su mes de priorización" },
    { label:"↗ Cert. Backlog",     valor:kpiBacklog,   color:"#2563eb",   kpi:"Cert. Backlog",   desc:"Priorizados en mes anterior, cert. después" },
    { label:"⏳ Pendientes",        valor:kpiPend,      color:"#7c3aed",   kpi:"Pendientes",      desc:"Sin certificar en el mes actual" },
    { label:"🔴 Bolsa Nodos",      valor:kpiSinEnt,    color:NARANJ,       kpi:"Bolsa Nodos",     desc:"Sin certificar de meses anteriores" },
    { label:"📦 Cert. sin prio",   valor:kpiSinPrio,   color:"#0891b2",   kpi:"Cert. sin prio",  desc:"Certificados sin ninguna programación" },
    { label:"Total Certificados",  valor:kpiTotalCert, color:"#059669",   kpi:null,            desc:"Cert. mismo mes + backlog + sin prio" },
  ];

  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"16px" }}>
        <div style={{ width:"4px", height:"18px", background:CLARO_RED, borderRadius:"2px" }} />
        <div style={{ fontSize:"14px", fontWeight:"700", color:CLARO_DARK }}>Detalle de nodos</div>
        <span style={{ fontSize:"11px", color:CLARO_MUTED, marginLeft:"4px" }}>— priorizados y certificados (incluye nodos cert. sin priorización)</span>
      </div>

      {/* Tarjetas KPI — igual tamaño, clic para filtrar */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"16px" }}>
        {kpis.map(({ label, valor, color, kpi, desc }) => {
          const activo = filtroKpi === kpi && kpi !== null;
          return (
            <div key={label}
              onClick={() => { if (kpi) { setFiltroKpi(activo ? null : kpi); setFiltroEstado(""); } }}
              title={desc || label}
              style={{
                flex:"1 1 0", minWidth:"110px", maxWidth:"180px",
                background: activo ? color : "white",
                borderRadius:"10px", padding:"10px 14px", textAlign:"center",
                border: activo ? `2px solid ${color}` : "1px solid #e2e8f0",
                borderTop: activo ? `2px solid ${color}` : `3px solid ${color}`,
                cursor: kpi ? "pointer" : "default",
                boxShadow: activo ? `0 2px 10px ${color}40` : "0 1px 3px rgba(0,0,0,0.06)",
                transition:"all 0.15s",
              }}>
              <div style={{ fontSize:"10px", fontWeight:"700", color: activo ? "white" : CLARO_MUTED, marginBottom:"4px", lineHeight:"1.3" }}>{label}</div>
              <div style={{ fontSize:"22px", fontWeight:"800", color: activo ? "white" : color, lineHeight:1 }}>{fmt(valor)}</div>
              {desc && <div style={{ fontSize:"8px", color: activo ? "rgba(255,255,255,0.7)" : CLARO_LIGHT, marginTop:"3px", lineHeight:"1.2" }}>{activo ? "✕ clic para limpiar" : desc}</div>}
            </div>
          );
        })}
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:"10px", flexWrap:"wrap", marginBottom:"14px", alignItems:"center" }}>
        {filtroKpi && (
          <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"5px 10px", borderRadius:"8px", background:"#f3f0ff", border:"1px solid #c4b5fd", fontSize:"11px", color:"#7c3aed", fontWeight:"600" }}>
            Filtrando: {filtroKpi}
            <span onClick={()=>setFiltroKpi(null)} style={{ cursor:"pointer", fontWeight:"700", fontSize:"13px" }}>✕</span>
          </div>
        )}
        <input type="text" placeholder="🔍 Buscar nodo..." value={busqueda} onChange={e=>setBusqueda(e.target.value)}
          style={{ padding:"8px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", color:CLARO_DARK, outline:"none", minWidth:"180px" }}
          onFocus={e=>e.target.style.borderColor=CLARO_RED} onBlur={e=>e.target.style.borderColor="#e2e8f0"} />
        <select value={filtroMes} onChange={e=>setFiltroMes(e.target.value)}
          style={{ padding:"8px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Todos los meses</option>
          {[...new Set(filas.map(n => n.mes_priorizacion).filter(Boolean))].sort().map(k => (
            <option key={k} value={k}>{getMesLabel(k)}</option>
          ))}
        </select>
        {!filtroKpi && (
          <select value={filtroEstado} onChange={e=>setFiltroEstado(e.target.value)}
            style={{ padding:"8px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
            <option value="">Todos los estados</option>
            <option value="Certificado">✓ Certificado</option>
            <option value="Cert. otro mes">↗ Cert. otro mes</option>
            <option value="Cert. sin prio">📦 Cert. sin priorización</option>
            <option value="Pendiente">⏳ Pendiente</option>
            <option value="Sin entrega">🔴 Sin entrega</option>
          </select>
        )}
        {(busqueda || filtroMes || filtroEstado || filtroKpi) && (
          <button onClick={()=>{ setBusqueda(""); setFiltroMes(""); setFiltroEstado(""); setFiltroKpi(null); }}
            style={{ padding:"7px 12px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:"#f8fafc", cursor:"pointer", color:CLARO_RED }}>
            ✕ Limpiar
          </button>
        )}
        <span style={{ fontSize:"12px", color:CLARO_LIGHT }}>{filas.length} registros</span>
        <button onClick={() => {
          const enc = (v) => `"${String(v ?? "").replace(/"/g,'""')}"`;
          const cols = ["Nodo","Mes Priorizacion","Calidad ICR","HHPP","Estado","Mes Certificacion","OT/CHG","Prioridad"];
          const rows = [cols.join(";")];
          filas.forEach(n => rows.push([
            enc(n.nodo), enc(n.mes_priorizacion || ""), enc(n.calidad_icr ?? ""),
            enc(n.hhpp ?? ""), enc(n.estado || ""), enc(n.mes_certificacion || ""),
            enc(n.ot_chg || ""), enc(n.prioridad || "")
          ].join(";")));
          const blob = new Blob(["\uFEFF" + rows.join("\n")], { type:"text/csv;charset=utf-8;" });
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `DetalleNodos_${filtroMes || "todos"}_${filtroEstado || estadoEfectivo || "todos"}.csv`;
          a.click();
        }}
          style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:CLARO_RED, cursor:"pointer", color:"white", display:"flex", alignItems:"center", gap:"5px", marginLeft:"auto" }}>
          ⬇ Descargar CSV
        </button>
      </div>

      {/* Tabla */}
      <div style={{ overflowX:"auto", borderRadius:"8px", border:"1px solid #e2e8f0" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"13px" }}>
          <thead>
            <tr style={{ background:CLARO_BG }}>
              {["Nodo","Mes priorización","Calidad ICR","HHPP","Estado","Mes certificación","OT/CHG","Prioridad"].map(h=>(
                <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:"700", color:CLARO_MUTED, fontSize:"11px", textTransform:"uppercase", letterSpacing:"0.05em", borderBottom:"1px solid #e2e8f0", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan="8" style={{ padding:"32px", textAlign:"center", color:CLARO_MUTED }}>Cargando...</td></tr>
            ) : slice.length === 0 ? (
              <tr><td colSpan="8" style={{ padding:"32px", textAlign:"center", color:CLARO_LIGHT, fontSize:"14px" }}>No se encontraron registros.</td></tr>
            ) : slice.map((n,i) => (
              <tr key={`${n.nodo}-${i}`} style={{ borderBottom:"1px solid #f1f5f9", background: n.estado==="Cert. sin prio" ? "#f0f9ff" : "white" }}
                onMouseEnter={e=>e.currentTarget.style.background=n.estado==="Cert. sin prio"?"#e0f2fe":CLARO_BG}
                onMouseLeave={e=>e.currentTarget.style.background=n.estado==="Cert. sin prio"?"#f0f9ff":"white"}>
                <td style={{ padding:"10px 14px", fontWeight:"700", color:CLARO_DARK }}>{n.nodo}</td>
                <td style={{ padding:"10px 14px", color:CLARO_MUTED }}>{n.mes_priorizacion?(MESES_LABEL[n.mes_priorizacion]||n.mes_priorizacion):"—"}</td>
                <td style={{ padding:"10px 14px", color:CLARO_DARK }}>{n.priorizacion_calidad||"—"}</td>
                <td style={{ padding:"10px 14px", color:CLARO_DARK }}>{n.hhpp?fmt(n.hhpp):"—"}</td>
                <td style={{ padding:"10px 14px" }}>{badgeEstado(n.estado)}</td>
                <td style={{ padding:"10px 14px", color:CLARO_MUTED }}>{n.mes_certificacion?(MESES_LABEL[n.mes_certificacion]||n.mes_certificacion):"—"}</td>
                <td style={{ padding:"10px 14px", color:CLARO_MUTED, fontFamily:"monospace", fontSize:"12px" }}>{n.ot_chg||"—"}</td>
                <td style={{ padding:"10px 14px", color:CLARO_MUTED, maxWidth:"160px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.prioridad||"—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:"14px", flexWrap:"wrap", gap:"10px" }}>
        <span style={{ fontSize:"13px", color:CLARO_MUTED }}>Página {paginaReal} de {totalPags}</span>
        <div style={{ display:"flex", gap:"8px" }}>
          <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={paginaReal===1}
            style={{ padding:"7px 16px", borderRadius:"8px", fontSize:"13px", fontWeight:"600", cursor:paginaReal===1?"not-allowed":"pointer", border:"1px solid #e2e8f0", background:paginaReal===1?CLARO_BG:"white", color:paginaReal===1?CLARO_LIGHT:CLARO_DARK }}>
            ‹ Anterior
          </button>
          <button onClick={()=>setPagina(p=>Math.min(totalPags,p+1))} disabled={paginaReal>=totalPags}
            style={{ padding:"7px 16px", borderRadius:"8px", fontSize:"13px", fontWeight:"600", cursor:paginaReal>=totalPags?"not-allowed":"pointer", border:`1px solid ${CLARO_RED}`, background:paginaReal>=totalPags?CLARO_BG:CLARO_RED, color:paginaReal>=totalPags?CLARO_LIGHT:"white" }}>
            Siguiente ›
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tabla Resumen Total — detalle completo con filtros ────────
function TablaResumenTotal() {
  const [datos,      setDatos]      = useState({ filas:[], aliados:[], ciudades:[], jefes:[], mesesPrio:[], mesesCert:[] });
  const [cargando,   setCargando]   = useState(true);

  // Filtros
  const [fAliado,   setFAliado]   = useState("");
  const [fCiudad,   setFCiudad]   = useState("");
  const [fJefe,     setFJefe]     = useState("");
  const [fMesPrio,  setFMesPrio]  = useState("");
  const [fMesCert,  setFMesCert]  = useState("");
  const [busqueda,  setBusqueda]  = useState("");

  // Paginación
  const [pagina,    setPagina]    = useState(1);
  const POR_PAG = 15;

  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      try {
        const p = new URLSearchParams();
        if (fAliado)  p.append("aliado",    fAliado);
        if (fCiudad)  p.append("ciudad",    fCiudad);
        if (fJefe)    p.append("jefe_zona", fJefe);
        if (fMesPrio) p.append("mes_prio",  fMesPrio);
        if (fMesCert) p.append("mes_cert",  fMesCert);
        const res  = await fetch(`${process.env.REACT_APP_API_URL}/priorizacion-huella-resumen-total?${p}`);
        const json = await res.json();
        setDatos(json);
        setPagina(1);
      } catch { setDatos({ filas:[], aliados:[], ciudades:[], jefes:[], mesesPrio:[], mesesCert:[] }); }
      finally { setCargando(false); }
    };
    cargar();
  }, [fAliado, fCiudad, fJefe, fMesPrio, fMesCert]);

  const mesMostrarCorto = (ym) => {
    if (!ym) return "—";
    const [y, m] = ym.split("-");
    const names = ["","Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
    return `${names[parseInt(m)] || m} ${y}`;
  };

  const estadoStyle = (est) => {
    if (est === "Certificado")    return { bg:"#f0fdf4", color:"#16a34a", txt:"✓ Certificado" };
    if (est === "Cert. otro mes") return { bg:"#eff6ff", color:"#2563eb", txt:"↗ Cert. otro mes" };
    if (est === "Sin entrega")    return { bg:"#fff7ed", color:NARANJ,    txt:"⚠ Sin entrega" };
    if (est === "Reprogramado")   return { bg:"#faf5ff", color:"#7c3aed", txt:"🔁 Reprogramado" };
    return                               { bg:"#f8fafc", color:ROJO,      txt:"⏳ Pendiente" };
  };

  // Búsqueda local
  const q = busqueda.toLowerCase().trim();
  const filasFiltradas = q
    ? (datos.filas || []).filter(n =>
        (n.nodo||"").toLowerCase().includes(q) ||
        (n.nombre_nodo||"").toLowerCase().includes(q) ||
        (n.aliado||"").toLowerCase().includes(q) ||
        (n.ciudad||"").toLowerCase().includes(q) ||
        (n.jefe_zona||"").toLowerCase().includes(q)
      )
    : (datos.filas || []);

  // KPIs desde filas filtradas
  const kCert  = filasFiltradas.filter(n => n.estado === "Certificado").length;
  const kOtro  = filasFiltradas.filter(n => n.estado === "Cert. otro mes").length;
  const kPend  = filasFiltradas.filter(n => n.estado === "Pendiente").length;
  const kBolsa = filasFiltradas.filter(n => n.estado === "Sin entrega").length;
  const kRepr  = filasFiltradas.filter(n => n.estado === "Reprogramado").length;
  const kTotal = filasFiltradas.length;
  const kHhpp  = filasFiltradas.reduce((s, n) => s + (Number(n.hhpp)||0), 0);
  const kTotCert = kCert + kOtro;

  // Paginación
  const totalPags  = Math.max(1, Math.ceil(filasFiltradas.length / POR_PAG));
  const paginaReal = Math.min(pagina, totalPags);
  const slice      = filasFiltradas.slice((paginaReal-1)*POR_PAG, paginaReal*POR_PAG);

  const hayFiltro = fAliado || fCiudad || fJefe || fMesPrio || fMesCert || busqueda;

  const limpiar = () => { setFAliado(""); setFCiudad(""); setFJefe(""); setFMesPrio(""); setFMesCert(""); setBusqueda(""); };

  const descargarCSV = () => {
    const enc = v => `"${String(v ?? "").replace(/"/g,'""')}"`;
    const cols = ["Nodo","Nombre Nodo","Aliado","Ciudad","Jefe Zona","Mes Priorización","Cal. ICR","HHPP","Estado","Mes Certificación","Fecha Cert.","OT/CHG"];
    const filas = [cols.join(";")];
    filasFiltradas.forEach(n => filas.push([
      enc(n.nodo), enc(n.nombre_nodo), enc(n.aliado), enc(n.ciudad), enc(n.jefe_zona),
      enc(n.mes_priorizacion ? mesMostrarCorto(n.mes_priorizacion) : ""),
      enc(n.priorizacion_calidad ?? ""), enc(n.hhpp ?? ""),
      enc(n.estado), enc(n.mes_certificacion ? mesMostrarCorto(n.mes_certificacion) : ""),
      enc(n.fecha_cert ?? ""), enc(n.ot_chg ?? "")
    ].join(";")));
    const blob = new Blob(["\uFEFF" + filas.join("\n")], { type:"text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `ResumenTotal_Huella.csv`; a.click();
  };

  const thSt = { padding:"9px 12px", textAlign:"left", fontWeight:"700", color:CLARO_MUTED, fontSize:"10px", textTransform:"uppercase", letterSpacing:"0.04em", borderBottom:"1px solid #e2e8f0", whiteSpace:"nowrap", background:CLARO_BG };

  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px", marginBottom:"20px", marginTop:"4px" }}>

      {/* Título */}
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"16px" }}>
        <div style={{ width:"4px", height:"20px", background:CLARO_RED, borderRadius:"2px" }} />
        <div>
          <div style={{ fontSize:"14px", fontWeight:"700", color:CLARO_DARK }}>Tabla Resumen Total — Detalle completo de nodos</div>
          <div style={{ fontSize:"11px", color:CLARO_MUTED, marginTop:"2px" }}>Todos los nodos priorizados con sus estados, filtros independientes por aliado, ciudad, jefe, mes de priorización y mes de certificación.</div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"16px" }}>
        {[
          { label:"🎯 Total priorizados",  valor:kTotal,    color:CLARO_DARK },
          { label:"✅ Certificados",        valor:kCert,     color:VERDE },
          { label:"↗ Cert. otro mes",      valor:kOtro,     color:"#2563eb" },
          { label:"📦 Total ejecutado",    valor:kTotCert,  color:"#0891b2" },
          { label:"⏳ Pendientes",          valor:kPend,     color:"#7c3aed" },
          { label:"🔴 Bolsa nodos",        valor:kBolsa,    color:NARANJ },
          { label:"🔁 Reprogramados",      valor:kRepr,     color:"#9333ea" },
          { label:"⏱ Total HHPP",         valor:fmt(kHhpp),color:CLARO_MUTED, noFmt:true },
        ].map(({ label, valor, color, noFmt }) => (
          <div key={label} style={{ flex:"1 1 0", minWidth:"100px", maxWidth:"160px", background:"white", borderRadius:"10px", padding:"10px 14px", textAlign:"center", border:"1px solid #e2e8f0", borderTop:`3px solid ${color}`, boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:"10px", fontWeight:"700", color:CLARO_MUTED, marginBottom:"4px", lineHeight:"1.3" }}>{label}</div>
            <div style={{ fontSize:"22px", fontWeight:"800", color, lineHeight:1 }}>{noFmt ? valor : fmt(valor)}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"14px", alignItems:"center" }}>
        <input type="text" placeholder="🔍 Buscar nodo, aliado, ciudad..." value={busqueda}
          onChange={e=>{setBusqueda(e.target.value);setPagina(1);}}
          style={{ padding:"7px 11px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, outline:"none", minWidth:"200px" }}
          onFocus={e=>e.target.style.borderColor=CLARO_RED} onBlur={e=>e.target.style.borderColor="#e2e8f0"} />

        <select value={fJefe} onChange={e=>setFJefe(e.target.value)}
          style={{ padding:"7px 11px", border:`1.5px solid ${fJefe?CLARO_RED:"#e2e8f0"}`, borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Todos los jefes</option>
          {(datos.jefes||[]).map(j=><option key={j} value={j}>{j}</option>)}
        </select>

        <select value={fAliado} onChange={e=>setFAliado(e.target.value)}
          style={{ padding:"7px 11px", border:`1.5px solid ${fAliado?CLARO_RED:"#e2e8f0"}`, borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Todos los aliados</option>
          {(datos.aliados||[]).map(a=><option key={a} value={a}>{a}</option>)}
        </select>

        <select value={fCiudad} onChange={e=>setFCiudad(e.target.value)}
          style={{ padding:"7px 11px", border:`1.5px solid ${fCiudad?CLARO_RED:"#e2e8f0"}`, borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">Todas las ciudades</option>
          {(datos.ciudades||[]).map(c=><option key={c} value={c}>{c}</option>)}
        </select>

        <select value={fMesPrio} onChange={e=>setFMesPrio(e.target.value)}
          style={{ padding:"7px 11px", border:`1.5px solid ${fMesPrio?CLARO_RED:"#e2e8f0"}`, borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">📅 Mes priorización</option>
          {(datos.mesesPrio||[]).map(m=><option key={m} value={m}>{mesMostrarCorto(m)}</option>)}
        </select>

        <select value={fMesCert} onChange={e=>setFMesCert(e.target.value)}
          style={{ padding:"7px 11px", border:`1.5px solid ${fMesCert?"#16a34a":"#e2e8f0"}`, borderRadius:"8px", fontSize:"12px", color:CLARO_DARK, background:"white", cursor:"pointer" }}>
          <option value="">✅ Mes certificación</option>
          {(datos.mesesCert||[]).map(m=><option key={m} value={m}>{mesMostrarCorto(m)}</option>)}
        </select>

        {hayFiltro && (
          <button onClick={limpiar}
            style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:"#f8fafc", cursor:"pointer", color:CLARO_RED }}>
            ✕ Limpiar
          </button>
        )}
        <span style={{ fontSize:"12px", color:CLARO_LIGHT }}>{filasFiltradas.length} registros</span>
        <button onClick={descargarCSV}
          style={{ padding:"7px 14px", borderRadius:"8px", fontSize:"11px", fontWeight:"700", border:`1.5px solid ${CLARO_RED}`, background:CLARO_RED, cursor:"pointer", color:"white", marginLeft:"auto" }}>
          ⬇ Descargar CSV
        </button>
      </div>

      {/* Tabla */}
      <div style={{ overflowX:"auto", borderRadius:"8px", border:"1px solid #e2e8f0" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px", tableLayout:"fixed" }}>
          <thead>
            <tr>
              <th style={{...thSt, width:"8%"}}>Nodo</th>
              <th style={{...thSt, width:"11%"}}>Nombre nodo</th>
              <th style={{...thSt, width:"9%"}}>Jefe zona</th>
              <th style={{...thSt, width:"9%"}}>Aliado</th>
              <th style={{...thSt, width:"7%"}}>Ciudad</th>
              <th style={{...thSt, width:"7%"}}>Mes prio.</th>
              <th style={{...thSt, width:"5%", textAlign:"right"}}>Cal. ICR</th>
              <th style={{...thSt, width:"5%", textAlign:"right"}}>HHPP</th>
              <th style={{...thSt, width:"10%"}}>Estado</th>
              <th style={{...thSt, width:"7%"}}>Mes cert.</th>
              <th style={{...thSt, width:"8%"}}>Fecha cert.</th>
              <th style={{...thSt, width:"7%"}}>OT / CHG</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan="12" style={{ padding:"32px", textAlign:"center", color:CLARO_MUTED }}>Cargando...</td></tr>
            ) : slice.length === 0 ? (
              <tr><td colSpan="12" style={{ padding:"32px", textAlign:"center", color:CLARO_LIGHT }}>No se encontraron registros.</td></tr>
            ) : slice.map((n, i) => {
              const es = estadoStyle(n.estado);
              return (
                <tr key={`${n.nodo}-${i}`}
                  style={{ borderBottom:"1px solid #f1f5f9" }}
                  onMouseEnter={e=>e.currentTarget.style.background=CLARO_BG}
                  onMouseLeave={e=>e.currentTarget.style.background="white"}>
                  <td style={{ padding:"8px 12px", fontWeight:"700", color:CLARO_DARK, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.nodo}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={n.nombre_nodo}>{n.nombre_nodo||"—"}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.jefe_zona||"—"}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.aliado||"—"}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{n.ciudad||"—"}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, whiteSpace:"nowrap" }}>{n.mes_priorizacion ? mesMostrarCorto(n.mes_priorizacion) : "—"}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_DARK, fontWeight:"600", textAlign:"right" }}>{n.priorizacion_calidad != null ? fmt(n.priorizacion_calidad) : "—"}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, textAlign:"right" }}>{n.hhpp != null ? fmt(n.hhpp) : "—"}</td>
                  <td style={{ padding:"8px 12px" }}>
                    <span style={{ display:"inline-block", fontSize:"10px", fontWeight:"700", padding:"2px 8px", borderRadius:"20px", background:es.bg, color:es.color }}>{es.txt}</span>
                  </td>
                  <td style={{ padding:"8px 12px", color: n.mes_certificacion ? "#16a34a" : CLARO_LIGHT, whiteSpace:"nowrap" }}>
                    {n.mes_certificacion ? mesMostrarCorto(n.mes_certificacion) : "—"}
                  </td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, whiteSpace:"nowrap" }}>{n.fecha_cert||"—"}</td>
                  <td style={{ padding:"8px 12px", color:CLARO_MUTED, fontFamily:"monospace", fontSize:"11px" }}>{n.ot_chg||"—"}</td>
                </tr>
              );
            })}

            {/* Fila total */}
            {!cargando && filasFiltradas.length > 0 && (
              <tr style={{ background:"#f3f4f6", borderTop:"2px solid #e5e7eb" }}>
                <td colSpan="6" style={{ padding:"9px 12px", fontWeight:"700", color:CLARO_DARK, fontSize:"12px" }}>TOTAL — {filasFiltradas.length} nodos</td>
                <td style={{ padding:"9px 12px", fontWeight:"700", color:CLARO_DARK, textAlign:"right" }}>—</td>
                <td style={{ padding:"9px 12px", fontWeight:"700", color:CLARO_MUTED, textAlign:"right" }}>{fmt(kHhpp)}</td>
                <td style={{ padding:"9px 12px", fontSize:"11px", color:CLARO_MUTED }}>
                  <span style={{ color:VERDE, fontWeight:"700" }}>✓{kCert}</span>
                  {" · "}
                  <span style={{ color:"#7c3aed", fontWeight:"700" }}>⏳{kPend}</span>
                  {" · "}
                  <span style={{ color:NARANJ, fontWeight:"700" }}>🔴{kBolsa}</span>
                </td>
                <td colSpan="3"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:"14px", flexWrap:"wrap", gap:"10px" }}>
        <span style={{ fontSize:"13px", color:CLARO_MUTED }}>Página {paginaReal} de {totalPags} · {filasFiltradas.length} registros</span>
        <div style={{ display:"flex", gap:"8px" }}>
          <button onClick={()=>setPagina(p=>Math.max(1,p-1))} disabled={paginaReal===1}
            style={{ padding:"7px 16px", borderRadius:"8px", fontSize:"13px", fontWeight:"600", cursor:paginaReal===1?"not-allowed":"pointer", border:"1px solid #e2e8f0", background:paginaReal===1?CLARO_BG:"white", color:paginaReal===1?CLARO_LIGHT:CLARO_DARK }}>
            ‹ Anterior
          </button>
          <button onClick={()=>setPagina(p=>Math.min(totalPags,p+1))} disabled={paginaReal>=totalPags}
            style={{ padding:"7px 16px", borderRadius:"8px", fontSize:"13px", fontWeight:"600", cursor:paginaReal>=totalPags?"not-allowed":"pointer", border:`1px solid ${CLARO_RED}`, background:paginaReal>=totalPags?CLARO_BG:CLARO_RED, color:paginaReal>=totalPags?CLARO_LIGHT:"white" }}>
            Siguiente ›
          </button>
        </div>
      </div>
    </div>
  );
}

export default PriorizacionHuella;